import { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import tracking from '@g4/g4-analytics';
import moment from '@g4/moment-utc';
import { hook, withValue } from '@g4/automation-hooks';
import get from 'lodash/get';

import GAClickEventTracker from '../../../../../components/GAClickEventTracker.js';
import { useTranslations } from '../../../../../graphql/components/TranslationsProvider.js';
import { waterBlue } from '@g4/boneyard/lib/theme/colors';
import {
  useBookingPath,
  useCart,
  useSSRQuery,
} from '../../../../../graphql/index.js';
import {
  VerticalSpacing,
  Box,
  Text,
  Popover,
  Media,
  VisuallyHidden,
  Spinner,
  useMedia,
} from '../../../../../components/Boneyard.js';
import Price from '@g4/next-furniture/Price/Price';
import Markdown from '../../../../../components/Markdown.js';
import FlightOperator from '../../../../../components/FlightOperator.js';
import PopoverContent from './flightItem/PopoverContent.js';
import FLIGHT_ITEM_QUERY from './flightItem/flightItem.query.graphql.js';

import PLANE_ICON from '../../../../../images/plane-icon.svg';
import OVERNIGHT_ICON from '../../../../../images/overnight-icon.svg';
import newFareFeatureIconCheckmark from '../../../../../images/ic-checkmark-white.png';

import {
  DEPARTING,
  RETURNING,
  WARNING_DEPARTING_AFTER_RETURNING,
  NEW_FARE_TIER,
  eventTypes,
  DEFAULT_FARE_NAME,
  PREMIUM_FARE_NAME,
} from '../../../constants.js';
import { sortOrder } from '../../../../../utils/constants/trackingConstants.js';
import { sortAncillariesByOrder, buildMobileBundleLabel } from '../../../../../utils/ancillaryUtils.js';
import { tripflexCode, ancillaryTypes } from '../../../../ancillariesPage/constants.js';

import {
  FLIGHT_NUMBER_LABEL,
  FLIGHT_NUMBER,
  FLIGHT_DEPARTURE_LABEL,
  FLIGHT_DEPARTURE_TIME,
  FLIGHT_ARRIVAL_LABEL,
  FLIGHT_ARRIVAL_TIME,
  SEATS_AVAILABILITY_TEXT,
  SEATS_AVAILABLE_TEXT,
  STRIKETHROUGH_FLIGHT_PRICE,
  FLIGHT_PRICE,
  LOYALTY_DISCOUNTED_FLIGHT_PRICE,
  SELECTED_FLIGHT,
  UNSELECTED_FLIGHT,
  PRICE_BOX,
  OVERNIGHT_FLIGHT_LABEL,
  OTP_POPOVER,
  DISABLED_FLIGHT,
  FLIGHTS_PAGE_BUNDLE_OPTION_OPEN,
} from '../../../../../../automationHooks.json';
import getFlightsRawProducts from '../../../getFlightsRawProducts.js';
import { getAffinityString } from '../../../../app/trackingItems.js';

import infoIconImage from '../../../../../images/info.svg';
import { getBundleRawProduct } from '../../../../bundlesPage/getBundleRawProducts.js';
import {
  FLIGHT_ITEM_WRAPPER_SELECTOR,
  shouldCloseBundlePanel,
  useNewFareImages,
} from './flightItemWithBundles.utils.js';

const propTypes = {
  isFlightsQueryLoading: PropTypes.bool.isRequired,
  theme: PropTypes.object,
  flightOption: PropTypes.object.isRequired,
  setFlightSelected: PropTypes.func,
  selected: PropTypes.bool,
  leg: PropTypes.oneOf(['departing', 'returning']),
  date: PropTypes.string,
  disableFlightIfSameDayBasedOnTime: PropTypes.bool,
  warning: PropTypes.object,
  deactivateWarning: PropTypes.func,
  providerId: PropTypes.string,
  isRedemptionEnabledForNavitaire: PropTypes.bool,
  flightSearchCriteria: PropTypes.object,
  googleAnalyticsData: PropTypes.object,
  selectedBundles: PropTypes.object,
  selectedFlightOption: PropTypes.object,
  onBundleSelect: PropTypes.func,
  bundleOffers: PropTypes.array,
};

const FlightBox = styled(Box)`
  display: flex;
  align-items: stretch;
  min-height: 182px;
`;

const SelectedBundleBox = styled(Box)`
  width: ${(props) =>
    `calc(100% + (${props.isMobile ? props.theme.spacing(1) : props.theme.spacing(2)} * 2))`};
  display: flex;
  background-color: #ffdd40;
  border-radius: 2px;
  align-items: center;
  align-self: stretch;
  padding: ${(props) =>
    props.isMobile ? props.theme.spacing(1) : props.theme.spacing(2)};
  margin-left: ${(props) =>
    `calc(${props.isMobile ? props.theme.spacing(1) : props.theme.spacing(2)} * -1)`};
  margin-right: ${(props) =>
    `calc(${props.isMobile ? props.theme.spacing(1) : props.theme.spacing(2)} * -1)`};
  margin-top: ${(props) => (props.selected ? props.theme.spacing(5) : 0)};
  justify-content: ${(props) => (props.isMobile ? 'center' : 'space-around')};
  gap: ${(props) => (props.isMobile ? '2px' : '0')};
  padding-top: ${(props) => (props.selected && props.isMobile ? '-3' : 1)};

  ${(props) =>
    props.isMobile &&
    `> *:last-child {
      margin-left: 8px;
    }`}
`;

const NoBundleSelectedBox = styled(Box)`
  width: ${(props) =>
    `calc(100% + (${props.isMobile ? props.theme.spacing(1) : props.theme.spacing(2)} * 2))`};
  display: flex;
  min-height: ${(props) => (props.isMobile ? '36px' : '42px')};
  align-items: center;
  justify-content: center;
  align-self: stretch;
  border-radius: 2px;
  background-color: #fffae5;
  margin-left: ${(props) =>
    `calc(${props.isMobile ? props.theme.spacing(1) : props.theme.spacing(2)} * -1)`};
  margin-right: ${(props) =>
    `calc(${props.isMobile ? props.theme.spacing(1) : props.theme.spacing(2)} * -1)`};
`;

const FlightWrapper = styled.div`
  cursor: pointer;
  position: relative;
  background-color: #fff;
  border-bottom: 1px solid
    ${(props) => props.theme.colors.flights.flightItem.divider};

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 3px;
    background: linear-gradient(
      90deg,
      rgba(1, 87, 155, 0.08) 0%,
      rgba(1, 87, 155, 0.18) 50%,
      rgba(1, 87, 155, 0.08) 100%
    );
  }

  &:first-of-type {
    border-top: 1px solid
      ${(props) => props.theme.colors.flights.flightItem.divider};
  }
`;

const FlightNumberBox = styled(Box)`
  flex-basis: 96px;
  flex-grow: 0;

  @media (max-width: 768px) {
    flex-basis: 68px;
  }

  ${(props) => props.theme.query.md} {
    flex-basis: 90px;
  }
  padding-left: ${(props) => (props.theme.query.md ? '3%' : '5%')};
`;

const FlightNumberText = styled(Text)`
  text-decoration: underline;
`;

const LabelHeader = styled.p`
  font-size: ${(props) => props.theme.toRem(16)};

  ${(props) => props.theme.query.md} {
    font-size: ${(props) => props.theme.toRem(16)};
  }
`;
const FlightTime = styled.p`
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: ${(props) => props.theme.toRem(21)};
  font-weight: 600;

  @media (max-width: 768px) {
    margin-top: 0;
    margin-bottom: 0;
  }

  > span {
    font-size: ${(props) => props.theme.toRem(12)};
    font-weight: normal;
    padding-left: ${(props) => props.theme.spacing(1)};
  }

  ${(props) => props.theme.query.md} {
    font-size: ${(props) => props.theme.toRem(28)};
    > span {
      font-size: ${(props) => props.theme.toRem(18)};
    }
  }
`;
const FlightDetailsBox = styled(Box)`
  flex-grow: 1;

  ${/* sc-selector */ Box} {
    flex-grow: 1;
  }
`;

const TimeBox = styled(Box)`
  flex-basis: 100%;
  align-self: center;

  ${(props) => props.theme.query.md} {
    flex-basis: 60%;
  }
  
  @media (min-width: 990px) and (max-width: 1120px) {
    flex: 1 1 0;
    flex-basis: auto;
    min-width: 0;
  }
`;

const MobileFlightSummaryRow = styled.div`
  display: flex;
  justify-content: space-evenly;
  border-bottom: ${(props) =>
    props.singleFareMobile
      ? 0
      : `1px solid ${props.theme.colors.flights.flightItem.divider}`};

  @media (max-width: 768px) {
    border-bottom: 0;
  }
`;

const MobileFareTilesRow = styled.div`
  display: flex;
  align-items: stretch;
  background-color: ${(props) =>
    props.singleFareMobile
      ? '#fff'
      : props.theme.colors.flights.flightItem.lightBackground};
  padding: ${(props) =>
    props.singleFareMobile ? '0 16px' : '0'};

  @media (max-width: 768px) {
    background-color: #fff;
    gap: ${(props) => (props.singleFareMobile ? '0' : props.theme.spacing(1))};
  }
`;

const SeatsTextBox = styled(Box)`
  align-self: center;
  flex-basis: 100%;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding-top: ${(props) => props.theme.spacing(1)};
  padding-bottom: ${(props) =>
    props.isFareTile ? props.theme.spacing(1) : 0};

  ${/* sc-selector */ Text} {
    max-width: 100%;
    margin: 0 auto;
  }

  ${(props) => props.theme.query.md} {
    flex-basis: ${(props) => (props.isFareTile ? 'auto' : '40%')};
    margin-top: ${(props) => (props.isFareTile ? props.theme.spacing(1) : 0)};
    padding-bottom: ${(props) =>
      props.isFareTile ? props.theme.spacing(2) : 0};

    ${/* sc-selector */ Text} {
      max-width: ${(props) => (props.isFareTile ? '150px' : '220px')};
    }
  }
`;
const MobileBundleSubTitle = styled(Box)`
  align-items: center;
  justify-content: center;
  font-size: ${(props) => props.theme.toRem(12)};
  color: ${(props) => props.theme.colors.flights.flightItem.textSecondary};
  padding-right: ${(props) => props.theme.spacing(3)};
`;

const DepartureBox = styled(Box)`
  flex-basis: 0%;
  align-self: flex-start;
`;

const ArrivalBox = styled(Box)`
  flex-basis: 10%;
  padding-left: ${(props) => props.theme.spacing(12)};
  padding-right: 0;
  background: url(${PLANE_ICON}) no-repeat left center;
  background-size: 20px;
  align-self: flex-start;

  ${(props) => props.theme.query.md} {
    padding-left: ${(props) => props.theme.spacing(14)};
    padding-right: 0;
    background-position: left center;
    background-size: 40px;
  }

  ${(props) => props.theme.query.lg} {
    padding-left: ${(props) => props.theme.spacing(18)};
    padding-right: 0;
  }
`;
const OvernightText = styled(Text)`
  line-height: ${(props) => props.theme.toRem(10)};
  padding-left: ${(props) => props.theme.toRem(12)};
  margin-top: ${(props) => props.theme.toRem(2)};
  background: url(${OVERNIGHT_ICON}) no-repeat left center;
  background-size: 10px;

  ${(props) => props.theme.query.md} {
    background-size: 15px;
    line-height: ${(props) => props.theme.toRem(15)};
    padding-left: ${(props) => props.theme.toRem(18)};
    margin-top: ${(props) => props.theme.toRem(5)};
  }
`;

const SelectedFlightText = styled(Text)`
  font-weight: ${(props) => (props.isMobile ? 500 : 600)};
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  text-align: center;
  padding-top: ${(props) => props.theme.spacing(2)};
`;

/* stylelint-disable no-descending-specificity */
const SelectFlightBox = styled(Box)`
  flex-basis: ${({ theme, asFareTile }) =>
    asFareTile ? '270px' : theme.flights.list.priceWidth.mobile};
  flex-shrink: 0;
  background: ${(props) =>
    props.isSelectedFare && props.asFareTile
      ? 'linear-gradient(315deg, #FFB100 0%, #FFD200 99.16%)'
      : props.isActive
        ? '#F0F4FB'
        : props.highlightSelectedBundle && props.asFareTile
          ? 'linear-gradient(315deg, #FFB100 0%, #FFD200 99.16%)'
          : props.theme.colors.flights.flightItem.lightBackground};
  border-top: ${(props) =>
    props.asFareTile
      ? `3px solid ${
          props.showPendingBorder ||
          (props.isActive && !props.isSelectedFare)
            ? '#01579B'
            : 'transparent'
        }`
      : '0'};
  border-left: ${(props) =>
    props.asFareTile
      ? `1px solid ${props.theme.colors.flights.flightItem.divider}`
      : '4px solid #fff'};
  ${(props) =>
    props.selected &&
    !props.asFareTile &&
    `
    background-image: ${props.theme.colors.flights.flightItem.selected};
  `}

  @media (max-width: 768px) {
    ${(props) =>
      props.asFareTile &&
      `
      flex: 1 1 0;
      min-width: 0;
      min-height: 120px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      padding: ${props.theme.spacing(1)} ${props.theme.spacing(1)} 0;
      margin: 0;
    `}
  }

  ${(props) => props.theme.query.md} {
    flex-basis: ${({ theme, asFareTile }) =>
      asFareTile ? '270px' : theme.flights.list.priceWidth.midSized};
    margin: ${(props) =>
      props.asFareTile
        ? `${props.theme.spacing(2)} ${props.hasRightMargin ? props.theme.spacing(1) : 0} 0 ${props.theme.spacing(1)}`
        : '0'};
  }

  ${(props) => props.theme.query.lg} {
    flex-basis: ${({ theme, asFareTile }) =>
      asFareTile ? '270px' : theme.flights.list.priceWidth.desktop};
    min-height: ${(props) =>
      props.selected ? '164px' : props.minHeight || '164px'};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: ${(props) =>
      props.asFareTile
        ? `${props.theme.spacing(3)} ${props.theme.spacing(2)} 0`
        : `${props.theme.spacing(2)} 0`};
  }

  @media (min-width: 990px) and (max-width: 1120px) {
    ${(props) =>
      props.asFareTile &&
      `
      flex-basis: 205px;
      min-width: 205px;
      margin: ${props.theme.spacing(1)} ${props.hasRightMargin ? props.theme.spacing(0.5) : 0} 0 ${props.theme.spacing(0.5)};
    `}
  }
`;

const FareOptionsColumn = styled(Box)`
  display: none;

  ${(props) => props.theme.query.md} {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    min-height: 164px;
  }

  @media (min-width: 990px) and (max-width: 1120px) {
    min-height: 156px;
    flex-shrink: 0;
  }
`;

const FareOptionSpacer = styled.div`
  min-width: 270px;
  margin: ${(props) =>
    `${props.theme.spacing(2)} 0 0 ${props.theme.spacing(1)}`};
`;

const FareOptionCard = styled.button`
  position: relative;
  border: 0;
  background: ${(props) =>
    props.isUnavailable
      ? '#FFFFFF'
      : props.isSelectedFare
        ? 'linear-gradient(315deg, #FFB100 0%, #FFD200 99.16%)'
        : props.isActive
          ? '#F0F4FB'
          : props.highlightSelectedBundle
            ? 'linear-gradient(315deg, #FFB100 0%, #FFD200 99.16%)'
            : props.theme.colors.flights.flightItem.lightBackground};
  border-top: 3px solid
    ${(props) =>
      props.isActive && !props.isSelectedFare ? '#01579B' : 'transparent'};
  border-left: 1px solid
    ${(props) =>
      props.isUnavailable
        ? '#FFFFFF'
        : props.theme.colors.flights.flightItem.divider};
  border-bottom: 0;
  border-right: 0;
  margin: ${(props) =>
    `${props.theme.spacing(2)} 0 0 ${props.theme.spacing(1)}`};
  padding: ${(props) =>
    `${props.theme.spacing(3)} ${props.theme.spacing(2)} 0 ${props.theme.spacing(3)}`};
  min-width: 270px;
  cursor: pointer;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: 768px) {
    flex: 1 1 0;
    min-width: 0;
    min-height: 120px;
    margin: 0;
    padding: ${(props) =>
      `${props.theme.spacing(1)} ${props.theme.spacing(1)} 0 ${props.theme.spacing(2)}`};
  }

  &:focus-visible {
    outline: ${(props) => props.theme.colors.flights.flightItem.focus} solid
      thin;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 1;
  }
`;

const FareOptionAddedBanner = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 36px;
  background-color: #f0d447;
  color: #000000;
  font-size: ${(props) => props.theme.toRem(28)};
  font-weight: 500;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing(1)};
`;

const FareOptionAddedText = styled.span`
  font-size: ${(props) => props.theme.toRem(14)};
  font-weight: 500;
  line-height: 1;
`;

const FareOptionLabel = styled(Text)`
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.flights.flightItem.black};
  margin-bottom: ${(props) => props.theme.spacing(1)};
  line-height: 1.2;
`;

const FareOptionHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${(props) => props.theme.spacing(2)};
  padding-top: ${(props) => props.theme.spacing(0.5)};

  @media (max-width: 768px) {
    margin-bottom: ${(props) => props.theme.spacing(1)};
  }
`;

const FareOptionChevron = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-right: 2px solid #01579b;
  border-bottom: 2px solid #01579b;
  transform: ${(props) => (props.isOpen ? 'rotate(-135deg)' : 'rotate(45deg)')};
  transition: transform 0.2s ease;
  margin-top: 3px;
  margin-right: ${(props) => (props.extraRightPadding ? '6px' : '2px')};
`;

const FarePriceValueWrap = styled.div`
  width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
  display: flex;
  justify-content: ${(props) => (props.center ? 'center' : 'flex-end')};
  text-align: ${(props) => (props.center ? 'center' : 'right')};
  font-family: Montserrat, Arial, sans-serif;
  line-height: 33px;
  letter-spacing: 0.625px;
  margin-top: ${(props) => (props.isNewFare ? props.theme.spacing(2) : 0)};

  @media (max-width: 768px) {
    line-height: 1.1;
  }
`;

const PriceBox = styled.div`
  width: 100%;
  height: ${(props) =>
    props.isFareTile
      ? 'auto'
      : props.strikePrice && props.isMobile
        ? '83%'
        : props.isMobile && props.noThanksLinkClicked
          ? '76%'
          : !props.noThanksLinkClicked && props.selected
            ? '80%'
            : props.noThanksLinkClicked
              ? '85%'
              : '70%'};

  position: relative;
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: 1.125rem;
  font-weight: 500;
  text-align: center;
  flex-direction: column;

  > p {
    position: relative;
    font-size: ${(props) => props.theme.toRem(12)};
    padding-top: ${(props) => props.theme.spacing(2)};
    padding-bottom: ${(props) => props.theme.spacing(1)};
  }

  .prices {
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
      &:last-of-type {
        margin-left: ${(props) => props.theme.spacing(1)};
      }

      del {
        /* stylelint-disable-next-line declaration-no-important */
        position: relative;
        display: inline-block;
        text-decoration-line: none; /* we must override the native strikethrough line and add our custom one */

        &:after {
          position: absolute;
          content: '';
          left: 0;
          top: 50%;
          right: 0;
          border-top: 2px solid;
          border-color: #626f79;
          transform: rotate(-25deg);
        }
      }
    }
  }

  /* Mobile specific centering */
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-top: ${(props) => (props.isFareTile ? props.theme.spacing(1) : 0)};

    .prices {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;

      > div {
        margin-left: 0;
        margin-bottom: 4px;
        text-align: center;

        &:last-of-type {
          margin-left: 0;
          margin-bottom: 0;
        }
      }
    }
  }

  ${(props) => props.theme.query.md} {
    > p {
      font-size: ${(props) => props.theme.toRem(16)};
    }
  }

  ${(props) => props.theme.query.lg} {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${({ theme: { spacing }, isFareTile }) =>
      isFareTile ? `0` : `${spacing(6)} 0 0`};

    > p {
      position: absolute;
      left: 0;
      top: ${(props) => props.theme.spacing(3)};
      width: 100%;
      padding: 0 ${(props) => props.theme.spacing(4)};
      font-weight: 600;
    }
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  top: auto;
  left: -10000px;
  height: 1px;
  width: 1px;
  overflow: hidden;

  &:focus + ${/* sc-selector */ FlightBox} {
    outline: ${(props) => props.theme.colors.flights.flightItem.focus} solid
      thin;
  }
`;

const BundleTitle = styled.div`
  font-weight: 500;
  margin-left: 40px;
  margin-bottom: 16px;
  font-size: 20px;
  color: ${(props) => props.theme.colors.flights.flightItem.black};
  padding-top: 20px;
`;

const BundleOptions = styled.div`
  display: flex;
  margin-left: 30px;
  margin-right: 30px;
  gap: 16px;
  i & > *:hover {
    border: 2px solid #f89728;
    background: #fef5e9;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BundleOption = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 2px solid ${(props) => props.theme.colors.flights.flightItem.divider};
  border-radius: 20px;
  background: white;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;

  ${(props) =>
    props.isSelected &&
    `
    border: 2px solid #F89728;
    background: #FEF5E9;
  `}

  &:hover {
    border: 2px solid #F89728;
    background: #FEF5E9;
  }

  &:hover button[data-hook*='addButton'] {
    background-color: ${(props) =>
      props.isSelected ? '#F89728' : '#FEF5E9'};
  }
`;

const BundleHeader = styled.div`
  padding: 16px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
`;

const BundleContent = styled.div`
  flex: 1;
  padding: 8px 12px 0px 12px;
`;

const BundleFeature = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 12px;
  line-height: 1.4;
`;

const InfoIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-left: 4px;
  max-width: none;
  color: #6b7280;
  flex-shrink: 0;

  &:before {
    content: ${(props) => props.imgSrc}
    font-size: 12px;
  }
`;

const FeatureText = styled.span`
  flex: 1;
`;

const NoThanksLink = styled.a`
  display: block;
  margin-right: 30px;
  text-align: right;
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: 14px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 10px 10px;
  background-color: transparent;
  border: 1px solid
    ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  border-radius: 10px;
  text-align: center;
  text-decoration: none;
  padding-bottom: 25px;
`;

const TabBox = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  height: 35px;
  max-width: 90px;
  background-color: ${(props) => props.color};
  border-radius: 8px;
`;

const BundleImage = styled.img`
  max-width: 90%;
`;

const AncillaryImage = styled.img`
  max-width: 10%;
  margin-right: ${(props) => (props.isBagIcon ? '13px' : '8px')};
  width: ${(props) => (props.isBagIcon ? '18px' : 'auto')};
  height: ${(props) => (props.isBagIcon ? '18px' : 'auto')};
`;

const MobileBundleCard = styled.div`
  border: 1px solid ${(props) => props.theme.colors.flights.flightItem.divider};
  border-radius: 20px;
  margin: 15px;
  background: white;

  ${(props) =>
    props.isSelected &&
    `
    border: 2px solid #F89728;
    background: #FEF5E9;
  `}
`;

const MobileBundleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
`;

const MobileBundleContent = styled.div`
  padding: 16px;
`;

const MobileBundleFeature = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AddButton = styled.button`
  background-color: ${(props) => (props.isSelected ? '#F89728' : 'white')};
  border: ${(props) => (props.isSelected ? '' : '1px solid #01579B')};
  color: ${(props) => (props.isSelected ? 'white' : '#01579B')};
  border-radius: 13px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoThanksButton = styled.button`
  && {
    display: block;
    appearance: none;
    -webkit-appearance: none;
    margin-top: 16px;
    margin-right: 30px;
    margin-bottom: 15px;
    min-height: auto;
    padding: 5px 10px;
    background-color: white;
    border: 1px solid
      ${(props) => props.theme.colors.flights.flightItem.lightBlue};
    border-radius: 8px;
    color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
    font-size: 14px;
    font-weight: 600;
    line-height: normal;
    white-space: nowrap;

    @media (max-width: 768px) {
      margin-top: 0;
      margin-right: 15px;
      margin-bottom: 0;
      min-height: 40px;
      padding: 8px 14px;
      background-color: #F9FBFD;
      border-color: #C7D3DF;
      border-radius: 10px;
      font-size: 16px;
      line-height: 1.1;
    }
  }
  cursor: pointer;
  text-align: center;

  > p {
    margin: 0;
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
  }

  &:hover {
    background-color: ${(props) =>
      props.theme.colors.flights.flightItem.lightBlue};
    color: white;
  }
`;

const BundleTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const ExpandCollapseLink = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const RefundPill = styled.div`
  box-sizing: border-box;
  border: 1px solid #00579B;
  border-radius: 18px;
  padding: 5px 6px;
  width: fit-content;
  background-color: #F0F4FB;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  font-size: 10px;
  font-weight: 500;
  font-family: Montserrat;
  text-align: center;
  color: #005695;
  line-height: 12px;

  @media (max-width: 1200px) {
    white-space: normal;
  }
`;
const NewFareCardImage = styled.img`
  flex: 0 0 50%;
  width: 100%;
  height: 100%;
  align-self: stretch;
  object-fit: cover;
  display: block;
  min-height: 0;
`;

const NewFareImageWrapper = styled.div`
  flex: 0 0 50%;
  width: 50%;
  position: relative;
  align-self: stretch;
  min-height: 0;
  overflow: hidden;
  border-radius: 20px 0 0 20px;

  @media (max-width: 768px) {
    display: block;
    width: 100%;
    flex: 0 0 auto;
    height: 218px;
    border-radius: 20px 20px 0 0;
  }
`;

const NewFareImageTint = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 20px 0 0 20px;

  @media (max-width: 768px) {
    border-radius: 20px 20px 0 0;
  }
`;

const NewFareImageBottomTint = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 18%;
  background: #004479cc;
  border-radius: 0 0 0 20px;

  @media (max-width: 768px) {
    height: 30%;
    border-radius: 0;
  }
`;

const NewFareImageBottomTextList = styled.div`
  position: absolute;
  bottom: 12px;
  left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 768px) {
    gap: 1px;
    bottom: 7px;
    left: 12px;
  }
`;

const NewFareImageBottomTitle = styled.div`
  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const NewFareImageBottomSubtitle = styled.div`
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  font-weight: 400;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const NewFareImageTextList = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const NewFareImageTextItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.2px;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  img {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const NewFareCardContent = styled.div`
  background: #ffffff;
  overflow: hidden;
  cursor: pointer;
  border-radius: 0 20px 20px 0;
  flex: 1 1 50%;
  width: 50%;
  margin: 0;
  box-sizing: border-box;
  border-top: 2px solid transparent;
  border-right: 2px solid transparent;
  border-bottom: 2px solid transparent;
  border-left: none;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  ${(props) =>
    props.isSelected &&
    `
    border-top: 2px solid #f89728;
    border-right: 2px solid #f89728;
    border-bottom: 2px solid #f89728;
    border-left: none;
    background: #fef5e9;
  `}

  @media (max-width: 768px) {
    width: 100%;
    border-radius: 0 0 20px 20px;
    border: 2px solid transparent;

    ${(props) =>
      props.isSelected &&
      `
      border: 2px solid #f89728;
    `}
  }
`;

const NewFareModeWrapper = styled.div`
  width: 70%;
  margin-left: 175px;
  margin-right: -12px;
  padding: 30px 0 20px;
  display: flex;
  align-items: stretch;
  overflow: hidden;
  border-radius: 20px;

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    padding: 12px 12px 16px;
    border: none;
    border-radius: 0;
    flex-direction: column;
  }
`;

const NewFareCard = styled.div`
  display: flex;
  border-radius: 20px;
  border: 1px solid ${(props) => props.theme.colors.flights.flightItem.divider};
  cursor: pointer;
  opacity: 0;
  animation: fadeInNewFare 0.15s ease-out forwards;

  @keyframes fadeInNewFare {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  &:hover ${NewFareCardContent} {
    background-color: #fef5e9;
    border-top: 2px solid #f89728;
    border-right: 2px solid #f89728;
    border-bottom: 2px solid #f89728;
    border-left: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;

    &:hover ${NewFareCardContent} {
      border: 2px solid #f89728;
    }
  }
`;

const NewFareCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 20px 12px;
`;

const NewFareCardTitle = styled.div`
  font-size: 20px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.flights.flightItem.black};
  margin-bottom: 4px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NewFareCardSubtitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  letter-spacing: 0.6px;
  text-transform: uppercase;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NewFareMobileLabel = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    font-size: 18px;
    font-weight: 600;
    color: #000000;
    padding: 8px 4px;
    letter-spacing: 0.4px;
    text-align: center;
  }
`;

const NewFareHighlightsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const NewFareHighlightPill = styled.div`
  border: 1px solid
    ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  border-radius: 999px;
  background: #eef1f5;
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 5px 10px;
`;

const NewFarePriceBlock = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const NewFarePriceAmount = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
`;

const NewFarePriceLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 400;
`;

const NewFareFeatureGrid = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px 16px;

  @media (max-width: 768px) {
    padding: 0 16px 12px;
  }
`;

const NewFareFeatureCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: ${(props) =>
    props.ancillaryType === 'TRIP_FLEX' ? 'none' : '1px solid #b1cee3'};
  box-sizing: border-box;
  min-height: ${(props) => {
    if (props.ancillaryType === 'BAGS') return '36px';
    if (props.ancillaryType === 'PRIORITY_BOARDING') return '56px';
    if (props.ancillaryType === 'TRIP_FLEX') return '40px';
    return '48px';
  }};
  padding: ${(props) => {
    if (props.ancillaryType === 'BAGS') return '4px 0';
    if (props.ancillaryType === 'PRIORITY_BOARDING') return '10px 0';
    if (props.ancillaryType === 'TRIP_FLEX') return '5px 0';
    return '8px 0';
  }};

  &:last-child {
    border-bottom: none;
  }
`;

const NewFareCellIcon = styled.img`
  width: ${(props) => (props.ancillaryType === 'BAGS' ? '20px' : '30px')};
  height: ${(props) => (props.ancillaryType === 'BAGS' ? '20px' : '30px')};
  margin-right: ${(props) => (props.ancillaryType === 'BAGS' ? '8px' : '0px')};
  flex-shrink: 0;
  margin-top: 0;
`;

const NewFareCellLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
  font-weight: 400;
  line-height: 1.4;
`;

const NewFareCellValue = styled.div`
  font-size: 12px;
  font-weight: 500;
  font-style: normal;
  color: #1f2937;
  line-height: 1.4;
`;

const TripFlexNewFareInfoBox = styled.div`
  max-width: 200px;
  margin: 2px 0 2px;
  padding: 3px 6px;
  background: #F0F4FB;
  border: 1px solid #00579B;
  border-radius: 13px;
  font-size: 10px;
  font-weight: 500;
  color: #01579b;
`;

const NewFareSelectButtonWrapper = styled.div`
  padding: 0 20px 20px;
  display: flex;
  justify-content: center;
`;

const NewFareSelectButton = styled.button`
  width: 100%;
  padding: 6px 0;
  background: ${(props) => (props.isSelected ? '#F19C38' : '#ffffff')};
  color: ${(props) => (props.isSelected ? '#ffffff' : '#01579B')};
  border: ${(props) => (props.isSelected ? 'none' : '1.5px solid #01579B')};
  border-radius: 8px;
  font-size: ${(props) => (props.isSelected ? '20px' : '14px')};
  font-weight: 500;
  cursor: pointer;
  letter-spacing: 0.3px;
  outline: none;

  .btn-text {
    display: inline;
  }
  .btn-icon {
    display: none;
  }
`;

const FlightItemWithAllegiantFirst = withTheme(
  ({
    showBundlesInfo = true,
    setFlightBundleInfoOpen,
    isFlightsQueryLoading,
    flightOption,
    selected,
    leg,
    date,
    setFlightSelected,
    theme,
    disabled,
    disableFlightIfSameDayBasedOnTime,
    warning,
    deactivateWarning,
    loyaltyDiscountedPrice,
    providerId,
    isRedemptionEnabledForNavitaire,
    flightSearchCriteria,
    googleAnalyticsData,
    oppositeLegSelection,
    selectedBundles,
    selectedFlightOption,
    onBundleSelect,
    bundleOffers,
    allAncillariesList,
  }) => {
    const flightEl = useRef(null);
    const flightWrapperRef = useRef(null);
    const { data } = useSSRQuery(FLIGHT_ITEM_QUERY);
    const seatsAvailabilityThreshold = get(
      data,
      'application.configurations.seatsAvailabilityThreshold'
    );

    const [isAllExpanded, setIsAllExpanded] = useState(false);
    const [expandedBundles, setExpandedBundles] = useState(new Set());
    const [newFareMode, setNewFareMode] = useState(false);
    const [userSelectedFareType, setUserSelectedFareType] = useState(null);
    const prevShowBundlesInfoRef = useRef(false);

    const translations = useTranslations();
    const { isStadium } = useBookingPath();
    const { cart } = useCart();
    const mq = useMedia();
    const isMobile = mq.lt('md');

    const {
      flightNumberLabel,
      departureTimeLabel,
      arrivalTimeLabel,
      limitedSeatsAvailability,
      availableSeats,
      selectedFlightText,
      isOvernight,
      selectFlightLabel,
      returnFlightBeforeArrival,
      loyaltyDiscountedPriceText,
      loyaltyDiscountedPricePointsText,
      loyaltyDiscountedPriceDefaultText,
    } = translations.flightItem;
    const { operatedByLabel } = translations.common;
    const { newFareLabelHTML, mainFareLabelHTML } = translations.flightsPage;

    const {
      bundleAndSaveDepartingFlightLabelHTML,
      bundleAndSaveReturningFlightLabelHTML,
      collapseAllLabelHTML,
      expandAllLabelHTML,
      addedSelectedBundleLabelHTML,
      noBundleLabelHTML,
      bagsIncludeLabelHTML,
      personalItemLabelHTML,
      carryOnLabelHTML,
      checkedBagLabelHTML,
      totalBundleSeatOptionsLabelHTML,
      bonusBundleSeatOptionsLabelHTML,
      basicBundleSeatOptionsLabelHTML,
      priorityBoardingLabelHTML,
      tripFlexLabelHTML,
      enhancedTripFlexLabelHTML,
      tripFlexOFOPLabelHTML,
      tripFlexOFOPInfoDescriptionHTML,
      tripFlexInfoDescription,
      priorityBoardingInfoDescription,
      skipBundleButtonLabelHTML,
      newFareDepartureLabelHTML,
      newFareReturnLabelHTML,
      newFareSeatOptionsLabelHTML,
      newFareCheckedBagLabelHTML,
      newFareBagsTooltipTextHTML,
      newFareBenefitsFirstPointLabelHTML,
      newFareBenefitsSecondPointLabelHTML,
      newFareBenefitsThirdPointLabelHTML,
      newFareBannerTitleHTML,
      newFareBannerDescriptionHTML,
      newFareRefundBenefitLabelHTML,
      newFareDepartureResponsiveLabelHTML,
      newFareReturnResponsiveLabelHTML,
      selectButtonLabelHTML,
      newFareTripflexLabelHTML,
      newFareAddedLabelHTML,
      newFareSeatOptionsTitleHTML,
      bundleSeatOptionsTitleHTML,
      mainSeatOptionResponsiveLabelHTML,
      allSeatOptionsResponsiveLabelHTML,
      tripFlexResponsiveLabelHTML,
      priorityBoardingResponsiveLabelHTML,
      enhancedTripFlexResponsiveLabelHTML,
    } = translations.flightItemWithBundle;

    const { bundlePageTextAssociatedWithCurrentPrice } =
      translations.bundlesPage;

    const operatedBy = flightOption?.flight?.operatedBy;
    const validSelected = selected && !disabled;
    const flightRouteDirection =
      flightOption.flight.origin.code + flightOption.flight.destination.code;
    const affinityString = getAffinityString(cart?.order);

    // Filter bundleOffers based on current flight's bundleReferences
    const flightBundleReferences = flightOption?.bundleReferences || [];
    const newFareFlightOption = (flightOption?.flightFares || []).find(
      (fare) => fare?.fareName === 'PR'
    );
    const newFareBundleReferences = newFareFlightOption?.bundleReferences || [];

    const getBundleOffersByReferences = (bundleReferences = []) =>
      bundleOffers && bundleOffers.length > 0
        ? bundleOffers
            .filter((bundle) =>
              bundleReferences.includes(bundle.bundleReference)
            )
            .sort(
              (departTier, returnTier) =>
                (departTier.tier || 0) - (returnTier.tier || 0)
            ) // Sort by tier ascending
        : [];

    const filteredBundleOffers = getBundleOffersByReferences(
      flightBundleReferences
    );
    const filteredNewFareBundleOffers = getBundleOffersByReferences(
      newFareBundleReferences
    );

    // Helper function to get icon from allAncillariesList by type
    const getAncillaryIcon = (type) => {
      const ancillary = allAncillariesList?.find((item) => item.type === type);
      return ancillary?.icon || null;
    };

    const mapBundleOfferToBundleOption = (bundle) => {
      // Group bag-related ancillaries
      const bagTypes = ['ONE_PERSONAL_ITEM', 'CARRY_ON_BAG', 'CHECK_IN_BAG'];
      const bagAncillaries =
        bundle.ancillaries?.filter((ancillary) =>
          bagTypes.includes(ancillary.type)
        ) || [];
      const otherAncillaries =
        bundle.ancillaries?.filter(
          (ancillary) => !bagTypes.includes(ancillary.type)
        ) || [];

      // Sort otherAncillaries using utility function
      const ancillaryOrder = ['SEAT', 'PRIORITY_BOARDING', 'TRIP_FLEX'];
      const sortedOtherAncillaries = sortAncillariesByOrder(
        otherAncillaries,
        ancillaryOrder
      );

      const resposiveOrdering = [...bagAncillaries, ...sortedOtherAncillaries];

      let mobileBundleAncillaries = '';
      if (isMobile) {
        const labelMap = {
          mainSeatOptionResponsiveLabelHTML,
          allSeatOptionsResponsiveLabelHTML,
          tripFlexResponsiveLabelHTML,
          priorityBoardingResponsiveLabelHTML,
          enhancedTripFlexResponsiveLabelHTML,
          personalItemLabelHTML,
          carryOnLabelHTML,
          checkedBagLabelHTML,
          bundleSeatOptionsTitleHTML,
          newFareSeatOptionsLabelHTML,
          tripFlexLabelHTML,
          priorityBoardingLabelHTML,
        };
        mobileBundleAncillaries = buildMobileBundleLabel(
          resposiveOrdering,
          bundle.tier,
          labelMap
        );
      }

      const features = [];

      // Add grouped bag feature if any bag ancillaries exist
      if (bagAncillaries.length > 0) {
        const bagItems = [];
        const personalItem = bagAncillaries.find(
          (a) => a.type === 'ONE_PERSONAL_ITEM'
        );
        const carryOn = bagAncillaries.find((a) => a.type === 'CARRY_ON_BAG');
        const checkedBag = bagAncillaries.find(
          (a) => a.type === 'CHECK_IN_BAG'
        );

        if (personalItem)
          bagItems.push(<Markdown source={personalItemLabelHTML} />);
        if (carryOn) bagItems.push(<Markdown source={carryOnLabelHTML} />);
        if (checkedBag)
          bagItems.push(<Markdown source={bundle.tier === NEW_FARE_TIER ? newFareCheckedBagLabelHTML : checkedBagLabelHTML} />);

        // Concatenate bagItems with "," separator
        const bagItemsSubtitle =
          bagItems.length > 0
            ? bagItems.map((item, index) => (
                <span key={index}>
                  {item}
                  {index < bagItems.length - 1 && ', '}
                </span>
              ))
            : bagItems;

        features.push({
          text: bagsIncludeLabelHTML,
          subtitle: bagItemsSubtitle,
          included: true,
          icon: getAncillaryIcon('CARRY_ON_BAG'), // Using carry-on as representative bag icon
          ancillaryType: 'BAGS',
          info: bundle.tier === NEW_FARE_TIER ? true : false, // Show info icon for bags only on highest tier
        });
      }

      // Add other ancillaries as separate features
      sortedOtherAncillaries.forEach((ancillary) => {
        const getAncillarySubtitle = (
          type,
          numberOfIncludedItems,
          bundleTier
        ) => {
          const subtitleMap = {
            SEAT: getSeatSubtitleByTier(bundleTier),
            TRIP_FLEX:
              ancillary.code === tripflexCode.ENHANCED_TRIP_FLEX
                ? enhancedTripFlexLabelHTML
                : tripFlexLabelHTML,
            PRIORITY_BOARDING: priorityBoardingLabelHTML,
          };

          return (
            subtitleMap[type] ||
            (numberOfIncludedItems ? `${numberOfIncludedItems} included` : '')
          );
        };

        const getSeatSubtitleByTier = (tier) => {
          const seatTierMap = {
            1: basicBundleSeatOptionsLabelHTML,
            2: bonusBundleSeatOptionsLabelHTML,
            3: totalBundleSeatOptionsLabelHTML,
          };

          return seatTierMap[tier] || 'Seat selection available';
        };

        // Check if ancillary type has only subtitle (no title)
        const hasOnlySubtitle = ['TRIP_FLEX', 'PRIORITY_BOARDING'].includes(
          ancillary.type
        );

        features.push({
          text: hasOnlySubtitle ? null : bundleSeatOptionsTitleHTML,
          subtitle: (
            <Markdown
              source={getAncillarySubtitle(
                ancillary.type,
                ancillary.numberOfIncludedItems,
                bundle.tier
              )}
            />
          ),
          included: true,
          icon: getAncillaryIcon(ancillary.type),
          hasOnlySubtitle: hasOnlySubtitle,
          info: hasOnlySubtitle,
          ancillaryType: ancillary.type,
        });

        // Setting the ancillary code property to each ancillary object in features array
        features.forEach((feature) => {
          const matchedItem = sortedOtherAncillaries.find(
            (item) => item.type === feature.ancillaryType
          );
          feature.code = matchedItem ? matchedItem.code : null;
          // Setting icon for enhanced tripflex
          if (
            matchedItem?.type === ancillaryTypes.TRIP_FLEX &&
            matchedItem?.code === tripflexCode.ENHANCED_TRIP_FLEX
          ) {
            feature.icon = matchedItem ? matchedItem.icon : null;
          }
        });
      });

      return {
        type: bundle.name?.toLowerCase() || 'unknown',
        title: bundle.name?.split(' ')[1] || 'Bundle',
        fullName: bundle.name || 'Bundle',
        price: bundle.bundlePricePerPerson?.amount?.toString() || '0',
        tier: bundle.tier || 1,
        icon: bundle.icon || bundle.banner,
        features: features,
        mobileBundleAncillaries: mobileBundleAncillaries,
      };
    };

    // Use filtered bundleOffers for this specific flight
    const bundleOptions =
      filteredBundleOffers.map((bundle) =>
        mapBundleOfferToBundleOption(bundle)
      ) || [];
    const newFareBundleOptions =
      filteredNewFareBundleOffers.map((bundle) =>
        mapBundleOfferToBundleOption(bundle)
      ) || [];

    // Highest-tier bundle shown in the new fare detail card
    const newFareBundle =
      newFareBundleOptions.length > 0
        ? newFareBundleOptions[newFareBundleOptions.length - 1]
        : bundleOptions.length > 0
          ? bundleOptions[bundleOptions.length - 1]
          : null;

    const newFareFeatures = (() => {
      if (!newFareBundle?.features?.length) {
        return [];
      }

      const seatFeature = {
        text: newFareSeatOptionsTitleHTML,
        subtitle: <Markdown source={newFareSeatOptionsLabelHTML} />,
        included: true,
        icon: useNewFareImages(data).newFareSeatIcon,
        hasOnlySubtitle: false,
        info: false,
        ancillaryType: 'SEAT',
      };

      const allFeatures = [
        ...newFareBundle.features.filter((f) => f.ancillaryType !== 'SEAT'),
        seatFeature,
      ];

      const FEATURE_PRIORITY = ['BAGS', 'SEAT', 'PRIORITY_BOARDING', 'TRIP_FLEX'];
      const priorityIndexMap = FEATURE_PRIORITY.reduce(
        (map, type, idx) => ((map[type] = idx), map),
        {}
      );
      return [...allFeatures].sort((leftFeature, rightFeature) => {
        const leftPriority =
          priorityIndexMap[leftFeature?.ancillaryType] ?? FEATURE_PRIORITY.length;
        const rightPriority =
          priorityIndexMap[rightFeature?.ancillaryType] ?? FEATURE_PRIORITY.length;
        return leftPriority - rightPriority;
      });
    })();

    let productList;
    let skipBundleProductList;
    let googleAnalyticsFlightMarketData;
    let selectedFlights;
    if (googleAnalyticsData.data) {
      const googleAnalyticsFlightMarketData =
        googleAnalyticsData.data.application.googleAnalytics.flightMarkets;
      const selectedFlights = {
        departing: leg === 'departing' ? flightOption : oppositeLegSelection,
        returning: leg === 'returning' ? flightOption : oppositeLegSelection,
      };

      skipBundleProductList = getFlightsRawProducts(
        selectedFlights,
        flightSearchCriteria,
        loyaltyDiscountedPrice,
        affinityString,
        googleAnalyticsFlightMarketData,
        leg,
        eventTypes.NO_BUNDLE_SELECTED
      );
    }

    const trackFareSelectionGA = (bundleTier, fareName) => {
      onBundleSelect({
        leg: leg,
        tier: bundleTier,
        dateKey: date,
      });

      setFlightSelected('DF');
      setFlightBundleInfoOpen(null);

      const selectedBundle = bundleOffers.find(
        (bundle) => bundle.tier === bundleTier
      );
      const bundleRawProduct = getBundleRawProduct(
        selectedBundle,
        flightSearchCriteria,
        affinityString,
        flightOption.flight
      ).items;
      const selectedFlights = {
        departing: leg === DEPARTING ? flightOption : oppositeLegSelection,
        returning: leg === RETURNING ? flightOption : oppositeLegSelection,
      };

      const departureFareName = leg === DEPARTING ? fareName : undefined;
      const returningFareName = leg === RETURNING ? fareName : undefined;

      productList = getFlightsRawProducts(
        selectedFlights,
        flightSearchCriteria,
        loyaltyDiscountedPrice,
        affinityString,
        googleAnalyticsFlightMarketData,
        leg,
        eventTypes.BUNDLE_SELECTED,
        departureFareName,
        returningFareName
      );

      tracking.trackSelectItem({
        name: sortOrder.DEFAULT,
        rawProducts: [...productList, bundleRawProduct],
      });
    };

    const handleBundleSelection = (bundleTier) => {
      onBundleSelect({ leg, tier: bundleTier, dateKey: date });
      setFlightSelected(DEFAULT_FARE_NAME);
      setFlightBundleInfoOpen(null);
      trackFareSelectionGA(bundleTier, DEFAULT_FARE_NAME);
    };

    const handleNoBundle = () => {
      onBundleSelect({
        leg: leg,
        tier: null,
        dateKey: date,
      });

      setFlightSelected('DF');
      setFlightBundleInfoOpen(null);
      tracking.trackSelectItem({
        name: sortOrder.DEFAULT,
        rawProducts: skipBundleProductList,
      });
    };

    const currentSelectedTier = date
      ? selectedBundles?.tierByDate?.[leg]?.[date]
      : selectedBundles?.[leg === 'departing' ? 'outboundTier' : 'inboundTier'];
    const hasNewFareOption = Boolean(newFareFlightOption);
    const currentOrderPassengerCount = cart?.order?.travelers?.length || 0;
    const hasEnoughNewFareSeats =
      Number(
        newFareFlightOption?.availableSeatsCount ?? Number.MAX_SAFE_INTEGER
      ) >= currentOrderPassengerCount;
    const isNewFareSelectable = hasNewFareOption && hasEnoughNewFareSeats;
    const shouldApplyNoNewFareArrivalPadding = !isNewFareSelectable;
    const selectedBundleOption = currentSelectedTier
      ? bundleOptions.find((bundle) => bundle.tier === currentSelectedTier)
      : null;

    const noThanksLinkClicked = validSelected && currentSelectedTier === null;
    const shouldHighlightMainBox =
      validSelected && !noThanksLinkClicked && Boolean(selectedBundleOption);

    const seatsAvailability = (withHooks = true) => {
      if (
        flightOption.availableSeatsCount < seatsAvailabilityThreshold ||
        disableFlightIfSameDayBasedOnTime
      ) {
        return (
          <Media>
            {(mq) => (
              <Text
                size={mq.lt('md') ? 11 : 16}
                weight={500}
                automationHook={withHooks ? SEATS_AVAILABILITY_TEXT.hook : null}
                color={
                  disableFlightIfSameDayBasedOnTime
                    ? theme.colors.flights.flightItem.text
                    : theme.colors.flights.flightItem.alert
                }
              >
                <Markdown
                  source={
                    disableFlightIfSameDayBasedOnTime
                      ? returnFlightBeforeArrival
                      : limitedSeatsAvailability
                  }
                  data={
                    !disableFlightIfSameDayBasedOnTime
                      ? {
                          available_seats:
                            '<strong>' +
                            flightOption.availableSeatsCount +
                            '</strong>',
                          seat:
                            flightOption.availableSeatsCount > 1
                              ? 'seats'
                              : 'seat',
                        }
                      : {}
                  }
                />
              </Text>
            )}
          </Media>
        );
      }
      return (
        <Media>
          {(mq) => (
            <Text
              size={mq.lt('md') ? 11 : 16}
              automationHook={withHooks ? SEATS_AVAILABLE_TEXT.hook : null}
              weight={500}
            >
              {availableSeats}
            </Text>
          )}
        </Media>
      );
    };

    const renderloyaltyDiscount = (mq) => {
      if (loyaltyDiscountedPrice === null) {
        return (
          <Box
            h={45}
            pl={mq.lte('md') ? 0 : 8}
            pr={mq.lte('md') ? 0 : 8}
            automationHook={withValue(
              LOYALTY_DISCOUNTED_FLIGHT_PRICE.hook,
              'default-text'
            )}
          >
            <Text
              size={mq.lt('md') ? 10 : 12}
              color={theme.colors.common.textSecondary}
              lh={1.5}
            >
              {loyaltyDiscountedPriceDefaultText}
            </Text>
          </Box>
        );
      }

      if (loyaltyDiscountedPrice >= 0) {
        return (
          <Box
            h={45}
            automationHook={withValue(
              LOYALTY_DISCOUNTED_FLIGHT_PRICE.hook,
              'text'
            )}
          >
            <Text
              size={mq.lt('md') ? 10 : 12}
              color={theme.colors.common.textSecondary}
            >
              <Markdown
                source={loyaltyDiscountedPriceText}
                data={{
                  pointsValue: `$${Number(loyaltyDiscountedPrice).toFixed(2)}`,
                }}
              />
            </Text>
            <Text
              size={mq.lt('md') ? 10 : 12}
              color={theme.colors.common.textSecondary}
              lh={1.5}
            >
              {loyaltyDiscountedPricePointsText}
            </Text>
          </Box>
        );
      }

      return null;
    };

    const setFocus = () => {
      flightEl.current.focus();
    };

    const handleMainClick = (event) => {
      event.stopPropagation();
      if (disabled) {
        return;
      }

      setNewFareMode(false);
      setUserSelectedFareType('main');
      if (currentSelectedTier === NEW_FARE_TIER) {
        onBundleSelect({
          leg,
          tier: null,
          dateKey: date,
        });
      }
      setFlightSelected('DF');
      setFlightBundleInfoOpen(flightOption.id, false);
      setFocus();
    };

    const handleFareOptionClick = (event) => {
      event.stopPropagation();
      if (disabled || !isNewFareSelectable) {
        return;
      }

      setNewFareMode(true);
      setUserSelectedFareType('new');
      setFlightBundleInfoOpen(flightOption.id, false);
      setFocus();
    };

    const handleNewFareCardClick = (event) => {
      event.stopPropagation();
      if (disabled || !isNewFareSelectable) {
        return;
      }

      trackFareSelectionGA(NEW_FARE_TIER, PREMIUM_FARE_NAME);

      // Toggle: if already selected, deselect NFC and go to no bundle state
      if (currentSelectedTier === NEW_FARE_TIER) {
        onBundleSelect({
          leg,
          tier: null,
          dateKey: date,
        });
        setFlightSelected('DF');
        setUserSelectedFareType(null);
        setFlightBundleInfoOpen(null);
      } else {
        onBundleSelect({
          leg,
          tier: NEW_FARE_TIER,
          dateKey: date,
        });
        setFlightSelected('PR');
        setFlightBundleInfoOpen(null);
      }
    };

    const handleNewFareCardKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleNewFareCardClick(event);
      }
    };

    const isCurrentLegNewFareSelected =
      validSelected &&
      (selectedFlightOption?.selectedFareName === 'PR' ||
        currentSelectedTier === NEW_FARE_TIER);
    const isNewFareView = userSelectedFareType === 'new';
    const isMainFareSelected = validSelected && !isCurrentLegNewFareSelected;
    const isNewFareSelected = isCurrentLegNewFareSelected;
    const isMainFarePendingBundleSelection =
      showBundlesInfo &&
      isMainFareSelected &&
      typeof currentSelectedTier === 'undefined';
    const isMainFareActive =
      !isCurrentLegNewFareSelected &&
      (userSelectedFareType === 'main' || (!isNewFareView && validSelected));
    const isNewFareActive =
      isNewFareSelectable && (isNewFareView || isCurrentLegNewFareSelected);
    const showAddedFareOptionBanner = isCurrentLegNewFareSelected;
    const isMainFarePanelOpen = showBundlesInfo && !isNewFareView;
    const isNewFarePanelOpen = showBundlesInfo && isNewFareView;

    const newFareCardPrice = Number(newFareFlightOption?.price || 0);
    const getPrice = (mq) => {
      return (
        <div>
          <Box
            d="flex"
            direction="row"
            wrap={true}
            alignItems="center"
            justifyContent="center"
            pb={noThanksLinkClicked ? 3 : selectedBundleOption ? 2 : 0}
            pt={!noThanksLinkClicked && !selectedBundleOption ? 2 : 0}
          >
            {flightOption.strikethruPrice && (
              <Box pr={mq.lte('md') ? 1 : 2}>
                <del
                  aria-label={'original price: ' + flightOption.strikethruPrice}
                >
                  <Price
                    automationHook={STRIKETHROUGH_FLIGHT_PRICE.hook}
                    value={flightOption.strikethruPrice}
                    currency={'$'}
                    currencyFontSize={mq.lt('md') ? 8 : 13}
                    currencyFontWeight={600}
                    decimalFontSize={mq.lt('md') ? 8 : 12}
                    decimalFontWeight={500}
                    priceFontSize={mq.lt('md') ? 12 : 24}
                    priceFontWeight={500}
                    color={'#626F79'}
                  />
                </del>
              </Box>
            )}
            <FarePriceValueWrap
              aria-label={'discounted price: ' + flightOption.price}
            >
              <Price
                automationHook={FLIGHT_PRICE.hook}
                value={flightOption.price}
                currency={'$'}
                currencyFontSize={mq.lt('md') ? 10 : 23}
                currencyFontWeight={700}
                decimalFontSize={mq.lt('md') ? 10 : 20}
                decimalFontWeight={700}
                priceFontSize={mq.lt('md') ? 24 : 44}
                priceFontWeight={700}
                color={'#01579B'}
              />
            </FarePriceValueWrap>
            {/**
             * display loyalty discount banner only for g4 flights
             * this condition will be removed in future
             */}
            {(providerId === 'g4' && renderloyaltyDiscount(mq)) ||
              (providerId === 'other' &&
                isRedemptionEnabledForNavitaire &&
                renderloyaltyDiscount(mq))}
          </Box>
        </div>
      );
    };

    const toggleBundleExpansion = (bundleType) => {
      setExpandedBundles((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(bundleType)) {
          newSet.delete(bundleType);
        } else {
          newSet.add(bundleType);
        }
        return newSet;
      });
    };

    const handleExpandCollapseAll = () => {
      if (isAllExpanded) {
        // Collapse all
        setExpandedBundles(new Set());
        setIsAllExpanded(false);
      } else {
        // Expand all
        const allBundleTypes = new Set(
          bundleOptions.map((bundle) => bundle.type)
        );
        setExpandedBundles(allBundleTypes);
        setIsAllExpanded(true);
      }
    };

    // When the bundle panel opens (showBundlesInfo transitions false to true), reset the
    // individual expansion state so the default-open behaviour is consistent whether the
    // data is fresh or returned from cache (revisited dates).
    useEffect(() => {
      if (showBundlesInfo && !prevShowBundlesInfoRef.current) {
        setExpandedBundles(new Set());
        setIsAllExpanded(false);
      }
      if (!showBundlesInfo) {
        setNewFareMode(false);
        setUserSelectedFareType(null);
      }
      prevShowBundlesInfoRef.current = showBundlesInfo;
    }, [showBundlesInfo]);

    useEffect(() => {
      const allBundleTypes = bundleOptions.map((bundle) => bundle.type);
      const allExpanded =
        allBundleTypes.length > 0 &&
        allBundleTypes.every((type) => expandedBundles.has(type));
      setIsAllExpanded(allExpanded);
    }, [expandedBundles, bundleOptions]);

    const renderBundleAddButton = (bundle, isSelected, hookSuffix) => (
      <AddButton
        isSelected={isSelected}
        data-hook={`flight-${flightOption.id}-bundleType-${bundle.type}-${hookSuffix}`}
        onClick={(e) => {
          e.stopPropagation();
          if (isSelected) {
            handleNoBundle();
          } else {
            setFlightSelected();
            setFlightBundleInfoOpen(null);
            handleBundleSelection(bundle.tier);
          }
        }}
      >
        {isSelected ? '✓' : 'ADD'}
      </AddButton>
    );

    return (
      <Media>
        {(mq) => (
          <FlightWrapper
            ref={flightWrapperRef}
            data-flight-item-with-bundles="true"
            {...(validSelected
              ? hook(SELECTED_FLIGHT.hook)
              : hook(
                  UNSELECTED_FLIGHT.hook,
                  `${leg}_${flightOption.flight.number}`
                ))}
            onClick={(e) => {
              if (!disabled) {
                if (
                  leg === DEPARTING &&
                  warning?.displayOn === RETURNING &&
                  warning?.key === WARNING_DEPARTING_AFTER_RETURNING
                ) {
                  deactivateWarning();
                }
                setNewFareMode(false);
                setUserSelectedFareType('main');
                setFlightBundleInfoOpen(flightOption.id, false);
                setFocus();
              }
              e.stopPropagation();
            }}
          >
            <VisuallyHidden>
              <label htmlFor={flightOption.id + '-radio'}>
                {selectFlightLabel} {flightOption.flight.number}
                {departureTimeLabel}{' '}
                {moment(flightOption.flight.departingTime).format('h:mm')}{' '}
                {moment(flightOption.flight.departingTime).format('a')}
                {arrivalTimeLabel}{' '}
                {moment(flightOption.flight.arrivalTime).format('h:mm')}{' '}
                {moment(flightOption.flight.arrivalTime).format('a')}
                {flightOption.flight.isOvernight && isOvernight}
                {seatsAvailability(false)}
                original price: {flightOption.strikethruPrice}
                discounted price: {flightOption.price}
              </label>
            </VisuallyHidden>
            <HiddenInput
              ref={flightEl}
              onChange={setFlightSelected}
              type="radio"
              name="radio-choice"
              id={flightOption.id + '-radio'}
              value={flightOption.id}
              checked={validSelected}
              disabled={disabled || disableFlightIfSameDayBasedOnTime}
            />
            <FlightBox
              automationHook={
                disabled
                  ? DISABLED_FLIGHT.hook +
                    '_' +
                    `${leg}_${flightOption.flight.number}`
                  : null
              }
              backgroundColor={
                disabled || disableFlightIfSameDayBasedOnTime
                  ? theme.colors.flights.flightItem.lightBackground
                  : '#fff'
              }
            >
              <FlightDetailsBox
                d="flex"
                direction={mq.lte('md') ? 'column' : 'row'}
                mr={mq.lte('md') ? 0 : 2}
                mb={mq.lte('md') ? -3 : 0}
              >
                <Box flexGrow={1} mb={mq.lte('md') ? 3 : 0}>
                  {mq.lte('md') ? (
                    <>
                      <Box>
                        <MobileFlightSummaryRow
                          singleFareMobile={!isNewFareSelectable}
                        >
                          <FlightNumberBox p={2} pt={2}>
                            <Box>
                              <LabelHeader {...hook(FLIGHT_NUMBER_LABEL.hook)}>
                                {flightNumberLabel}
                              </LabelHeader>
                            </Box>
                            <Box>
                              <Popover
                                content={
                                  <PopoverContent flightOption={flightOption} />
                                }
                                label={'popover'}
                                arrowSize={mq.lte('sm') ? 0 : 50}
                                arrowOffset="-50px"
                                backgroundColor={
                                  theme.colors.popover.background
                                }
                                maxWidth="530px"
                                showClose={mq.lte('sm')}
                                automationHook={
                                  validSelected
                                    ? withValue(
                                        OTP_POPOVER.hook,
                                        `selected_${leg}`
                                      )
                                    : undefined
                                }
                              >
                                <GAClickEventTracker
                                  onClick={() =>
                                    tracking.trackEvent(
                                      'site_click_tracking',
                                      'Flight Chooser',
                                      `OTP | ${flightRouteDirection} Flight ${flightOption.flight.number}`
                                    )
                                  }
                                >
                                  <FlightNumberText
                                    size={16}
                                    color={
                                      theme.colors.flights.flightItem.lightBlue
                                    }
                                    weight={600}
                                    automationHook={FLIGHT_NUMBER.hook}
                                  >
                                    {flightOption.flight.number}
                                  </FlightNumberText>
                                </GAClickEventTracker>
                              </Popover>
                            </Box>
                          </FlightNumberBox>
                          <Box flexGrow={1}>
                            <TimeBox d="column" pr={3} pt={2} pb={2}>
                              <Box d="flex" flexGrow={1}>
                                <DepartureBox>
                                  <LabelHeader
                                    {...hook(FLIGHT_DEPARTURE_LABEL.hook)}
                                  >
                                    {departureTimeLabel}
                                  </LabelHeader>
                                  <FlightTime
                                    {...hook(FLIGHT_DEPARTURE_TIME.hook)}
                                  >
                                    {moment(
                                      flightOption.flight.departingTime
                                    ).format('h:mm')}
                                    <span>
                                      {moment(flightOption.flight.departingTime)
                                        .format('a')
                                        .toUpperCase()}
                                    </span>
                                  </FlightTime>
                                </DepartureBox>
                                <ArrivalBox
                                  noNewFare={shouldApplyNoNewFareArrivalPadding}
                                >
                                  <LabelHeader
                                    {...hook(FLIGHT_ARRIVAL_LABEL.hook)}
                                  >
                                    {arrivalTimeLabel}
                                  </LabelHeader>
                                  <FlightTime
                                    {...hook(FLIGHT_ARRIVAL_TIME.hook)}
                                  >
                                    {moment(
                                      flightOption.flight.arrivalTime
                                    ).format('h:mm')}
                                    <span>
                                      {moment(flightOption.flight.arrivalTime)
                                        .format('a')
                                        .toUpperCase()}
                                    </span>
                                  </FlightTime>
                                  {flightOption.flight.isOvernight && (
                                    <OvernightText
                                      automationHook={
                                        OVERNIGHT_FLIGHT_LABEL.hook
                                      }
                                      size={10}
                                      color={
                                        theme.colors.flights.flightItem
                                          .lightBlue
                                      }
                                    >
                                      {isOvernight}
                                    </OvernightText>
                                  )}
                                </ArrivalBox>
                              </Box>
                            </TimeBox>
                          </Box>
                        </MobileFlightSummaryRow>
                        <MobileFareTilesRow
                          singleFareMobile={!isNewFareSelectable}
                        >
                          <SelectFlightBox
                            selected={validSelected}
                            highlightSelectedBundle={
                              shouldHighlightMainBox &&
                              !isCurrentLegNewFareSelected
                            }
                            asFareTile
                            singleFareMobile={!isNewFareSelectable}
                            hasRightMargin={isNewFareSelectable}
                            showPendingBorder={isMainFarePendingBundleSelection}
                            isActive={isMainFareActive}
                            isSelectedFare={isMainFareSelected}
                            onClick={handleMainClick}
                            style={{ cursor: 'pointer', borderLeft: 0 }}
                          >
                            <>
                              <FareOptionHeader>
                                <FareOptionLabel as="div" size={12}>
                                  <Markdown source={mainFareLabelHTML} />
                                </FareOptionLabel>
                                <FareOptionChevron
                                  extraRightPadding
                                  isOpen={isMainFarePanelOpen}
                                  aria-hidden="true"
                                />
                              </FareOptionHeader>
                              <PriceBox
                                {...hook(PRICE_BOX.hook)}
                                noThanksLinkClicked={noThanksLinkClicked}
                                isFareTile
                              >
                                <Box
                                  className="prices"
                                  d="flex"
                                  direction={mq.lt('lg') ? 'column' : 'row'}
                                >
                                  {isFlightsQueryLoading ? (
                                    <Spinner
                                      size="small"
                                      appearance="dark"
                                      automationHook={withValue(
                                        'price-box',
                                        'spinner'
                                      )}
                                    />
                                  ) : !isStadium ? (
                                    getPrice(mq)
                                  ) : (
                                    <p style={{ paddingTop: '12px' }}>
                                      <Text weight={600} wrap={true} size={20}>
                                        <span style={{ fontSize: '15px' }}>
                                          {'Included'}
                                        </span>
                                      </Text>
                                    </p>
                                  )}
                                </Box>
                                <SeatsTextBox
                                  isFareTile
                                  d="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  {seatsAvailability()}
                                </SeatsTextBox>
                                {validSelected &&
                                !isCurrentLegNewFareSelected &&
                                !noThanksLinkClicked &&
                                selectedBundleOption ? (
                                  <SelectedBundleBox
                                    selected={selectedBundleOption}
                                    automationHook={`flight-${flightOption.id}-selectedBundle-${selectedBundleOption?.type}`}
                                    isMobile
                                  >
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <circle
                                        cx="12"
                                        cy="12"
                                        r="12"
                                        fill="#058027"
                                      />
                                      <path
                                        d="M7 13.5L10.5 17L17 10"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <Text
                                      weight={600}
                                      color={
                                        theme.colors.flights.flightItem
                                          .lightBlue
                                      }
                                      size={12}
                                    >
                                      <Markdown source={newFareAddedLabelHTML}></Markdown>
                                    </Text>
                                    <Text
                                      weight={600}
                                      color={
                                        theme.colors.flights.flightItem
                                          .lightBlue
                                      }
                                      size={12}
                                    >
                                      + ${selectedBundleOption.price}
                                    </Text>
                                  </SelectedBundleBox>
                                ) : (
                                  validSelected &&
                                  !isCurrentLegNewFareSelected &&
                                  noThanksLinkClicked && (
                                    <NoBundleSelectedBox
                                      automationHook={`flight-${flightOption.id}-noBundleSelected`}
                                    >
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 28 28"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{ marginRight: 8 }}
                                      >
                                        <circle
                                          cx="14"
                                          cy="14"
                                          r="9"
                                          stroke="#222B2E"
                                          strokeWidth="2"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                          style={{ marginRight: 8 }}
                                        />
                                        <text
                                          x="14"
                                          y="20"
                                          textAnchor="middle"
                                          fontSize="18"
                                          fontWeight="bold"
                                          fill="#222B2E"
                                          fontFamily="Arial, sans-serif"
                                        >
                                          !
                                        </text>
                                      </svg>
                                      <Text
                                        size={12}
                                        color="#222B2E"
                                        weight={500}
                                        style={{
                                          fontFamily:
                                            'Montserrat, Arial, sans-serif',
                                          letterSpacing: '0.5px',
                                        }}
                                      >
                                        <Markdown source={noBundleLabelHTML} />
                                      </Text>
                                    </NoBundleSelectedBox>
                                  )
                                )}
                              </PriceBox>
                            </>
                          </SelectFlightBox>
                          {isNewFareSelectable && (
                            <FareOptionCard
                              type="button"
                              isUnavailable={!isNewFareSelectable}
                              newFareMode={newFareMode}
                              isActive={isNewFareActive}
                              isSelectedFare={isNewFareSelected}
                              highlightSelectedBundle={showAddedFareOptionBanner}
                              onClick={handleFareOptionClick}
                              data-hook={`flight-${flightOption.id}-new-fare-option`}
                              aria-label="New fare option"
                              disabled={!isNewFareSelectable || disabled}
                            >
                              {showAddedFareOptionBanner &&
                                isNewFareSelectable && (
                                  <FareOptionAddedBanner>
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <circle
                                        cx="12"
                                        cy="12"
                                        r="12"
                                        fill="#058027"
                                      />
                                      <path
                                        d="M7 13.5L10.5 17L17 10"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <FareOptionAddedText>
                                      <Markdown source={newFareAddedLabelHTML} />
                                    </FareOptionAddedText>
                                  </FareOptionAddedBanner>
                                )}
                              <FareOptionHeader>
                                <FareOptionLabel as="div" size={12}>
                                  <Markdown source={newFareLabelHTML} />
                                </FareOptionLabel>
                                {isNewFareSelectable && (
                                  <FareOptionChevron
                                    isOpen={isNewFarePanelOpen}
                                    aria-hidden="true"
                                  />
                                )}
                              </FareOptionHeader>
                              <FarePriceValueWrap center fullWidth isNewFare={isNewFareSelectable}>
                                <Price
                                  value={newFareCardPrice}
                                  currency={'$'}
                                  currencyFontSize={10}
                                  currencyFontWeight={700}
                                  decimalFontSize={10}
                                  decimalFontWeight={700}
                                  priceFontSize={24}
                                  priceFontWeight={700}
                                  color={'#01579B'}
                                />
                              </FarePriceValueWrap>
                            </FareOptionCard>
                          )}
                        </MobileFareTilesRow>
                        {operatedBy && (
                          <FlightOperator
                            operatedBy={operatedBy}
                            operatedByLabel={operatedByLabel}
                            display="flex"
                            weight={500}
                          ></FlightOperator>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Box>
                      <Box flexGrow={1} d="flex" justifyContent="space-between">
                        <FlightNumberBox
                          pt={mq.lt('md') ? 4 : 13}
                          pb={mq.lt('md') ? 4 : 13}
                          pr={mq.lt('md') ? 4 : 8}
                        >
                          <Box>
                            <LabelHeader style={{ fontWeight: 600 }} {...hook(FLIGHT_NUMBER_LABEL.hook)}>
                              {flightNumberLabel} 
                            </LabelHeader>
                          </Box>
                          <VerticalSpacing size={2} />
                          <Box>
                            <Popover
                              content={
                                <PopoverContent flightOption={flightOption} />
                              }
                              label={'popover'}
                              arrowSize={mq.lte('sm') ? 0 : 50}
                              arrowOffset="-50px"
                              backgroundColor={theme.colors.popover.background}
                              maxWidth="530px"
                              showClose={mq.lte('sm')}
                              automationHook={
                                validSelected
                                  ? withValue(
                                      OTP_POPOVER.hook,
                                      `selected_${leg}`
                                    )
                                  : undefined
                              }
                            >
                              <GAClickEventTracker
                                onClick={() =>
                                  tracking.trackEvent(
                                    'site_click_tracking',
                                    'Flight Chooser',
                                    `OTP | ${flightRouteDirection} Flight ${flightOption.flight.number}`
                                  )
                                }
                              >
                                <FlightNumberText
                                  size={mq.lt('md') ? 16 : 22}
                                  color={
                                    theme.colors.flights.flightItem.lightBlue
                                  }
                                  weight={600}
                                  automationHook={FLIGHT_NUMBER.hook}
                                >
                                  {flightOption.flight.number}
                                </FlightNumberText>
                              </GAClickEventTracker>
                            </Popover>
                          </Box>
                        </FlightNumberBox>
                        <TimeBox d="flex">
                          <Box d="flex" flexGrow={1}>
                            <Box d="flex" flexGrow={1}>
                              <DepartureBox pl={12}>
                                <LabelHeader
                                  style={{ fontWeight: 600 }}
                                  {...hook(FLIGHT_DEPARTURE_LABEL.hook)}
                                >
                                  {departureTimeLabel}
                                </LabelHeader>
                                <VerticalSpacing size={2} />

                                <FlightTime
                                  {...hook(FLIGHT_DEPARTURE_TIME.hook)}
                                >
                                  {moment(
                                    flightOption.flight.departingTime
                                  ).format('h:mm')}
                                  <span>
                                    {moment(flightOption.flight.departingTime)
                                      .format('a')
                                      .toUpperCase()}
                                  </span>
                                </FlightTime>
                                {!mq.lte('md') && operatedBy && (
                                  <FlightOperator
                                    operatedBy={operatedBy}
                                    operatedByLabel={operatedByLabel}
                                    display="flex"
                                    weight={500}
                                  ></FlightOperator>
                                )}
                              </DepartureBox>
                              <ArrivalBox
                                noNewFare={shouldApplyNoNewFareArrivalPadding}
                              >
                                <LabelHeader
                                  style={{ fontWeight: 600 }}
                                  {...hook(FLIGHT_ARRIVAL_LABEL.hook)}
                                >
                                  {arrivalTimeLabel}
                                </LabelHeader>
                                <VerticalSpacing size={2} />
                                <FlightTime {...hook(FLIGHT_ARRIVAL_TIME.hook)}>
                                  {moment(
                                    flightOption.flight.arrivalTime
                                  ).format('h:mm')}
                                  <span>
                                    {moment(flightOption.flight.arrivalTime)
                                      .format('a')
                                      .toUpperCase()}
                                  </span>
                                </FlightTime>
                                {flightOption.flight.isOvernight && (
                                  <OvernightText
                                    automationHook={OVERNIGHT_FLIGHT_LABEL.hook}
                                    size={mq.lte('md') ? 10 : 12}
                                    color={
                                      theme.colors.flights.flightItem.lightBlue
                                    }
                                  >
                                    {isOvernight}
                                  </OvernightText>
                                )}
                              </ArrivalBox>
                            </Box>
                          </Box>
                        </TimeBox>

                        {!isNewFareSelectable && (
                          <FareOptionsColumn>
                            <FareOptionSpacer aria-hidden="true" />
                          </FareOptionsColumn>
                        )}

                        <SelectFlightBox
                          selected={validSelected}
                          highlightSelectedBundle={
                            shouldHighlightMainBox &&
                            !isCurrentLegNewFareSelected
                          }
                          asFareTile
                          hasRightMargin={isNewFareSelectable}
                          showPendingBorder={isMainFarePendingBundleSelection}
                          isActive={isMainFareActive}
                          isSelectedFare={isMainFareSelected}
                          onClick={handleMainClick}
                          style={{ cursor: 'pointer' }}
                        >
                          <>
                            <FareOptionHeader>
                              <FareOptionLabel as="div" size={14}>
                                <Markdown source={mainFareLabelHTML} />
                              </FareOptionLabel>
                              <FareOptionChevron
                                extraRightPadding
                                isOpen={isMainFarePanelOpen}
                                aria-hidden="true"
                              />
                            </FareOptionHeader>
                            <PriceBox
                              {...hook(PRICE_BOX.hook)}
                              noThanksLinkClicked={noThanksLinkClicked}
                              isFareTile
                            >
                              <Box
                                className="prices"
                                d="flex"
                                direction={mq.lt('lg') ? 'column' : 'row'}
                              >
                                {isFlightsQueryLoading ? (
                                  <Spinner
                                    size="small"
                                    appearance="dark"
                                    automationHook={withValue(
                                      'price-box',
                                      'spinner'
                                    )}
                                  />
                                ) : !isStadium ? (
                                  getPrice(mq)
                                ) : (
                                  <p
                                    style={
                                      mq.lt('md')
                                        ? { paddingTop: '60px' }
                                        : { paddingTop: '12px' }
                                    }
                                  >
                                    <Text
                                      weight={600}
                                      wrap={true}
                                      size={mq.lt('md') ? 20 : 50}
                                    >
                                      <span style={{ fontSize: '15px' }}>
                                        {'Included'}
                                      </span>
                                    </Text>
                                  </p>
                                )}
                              </Box>
                              <SeatsTextBox
                                isFareTile
                                d="flex"
                                justifyContent="center"
                                alignItems="center"
                              >
                                {seatsAvailability()}
                              </SeatsTextBox>
                              {validSelected &&
                              !isCurrentLegNewFareSelected &&
                              !noThanksLinkClicked &&
                              selectedBundleOption ? (
                                <SelectedBundleBox
                                  selected={selectedBundleOption}
                                  automationHook={`flight-${flightOption.id}-selectedBundle-${selectedBundleOption?.type}`}
                                >
                                  <svg
                                    width={mq.lt('md') ? '20' : '24'}
                                    height={mq.lt('md') ? '20' : '24'}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={
                                      mq.lt('md') ? {} : { marginRight: 8 }
                                    }
                                  >
                                    <circle
                                      cx="12"
                                      cy="12"
                                      r="12"
                                      fill="#058027"
                                    />
                                    <path
                                      d="M7 13.5L10.5 17L17 10"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  <Text
                                    weight={500}
                                    size={mq.lt('md') ? 10 : 14}
                                    color="#000000"
                                    style={
                                      mq.lt('md') ? {} : { marginRight: 8 }
                                    }
                                  >
                                    <Markdown
                                      source={addedSelectedBundleLabelHTML}
                                      data={{
                                        selectedBundleName:
                                          selectedBundleOption.title,
                                      }}
                                    />
                                  </Text>
                                  <Text
                                    weight={500}
                                    color={
                                      theme.colors.flights.flightItem.lightBlue
                                    }
                                    size={mq.lt('md') ? 10 : 14}
                                  >
                                    + ${selectedBundleOption.price}
                                  </Text>
                                </SelectedBundleBox>
                              ) : (
                                validSelected &&
                                !isCurrentLegNewFareSelected &&
                                noThanksLinkClicked && (
                                  <NoBundleSelectedBox
                                    automationHook={`flight-${flightOption.id}-noBundleSelected`}
                                  >
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 28 28"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                      style={{ marginRight: 8 }}
                                    >
                                      <circle
                                        cx="14"
                                        cy="14"
                                        r="13"
                                        stroke="#222B2E"
                                        strokeWidth="2"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{ marginRight: 8 }}
                                      />
                                      <text
                                        x="14"
                                        y="19"
                                        textAnchor="middle"
                                        fontSize="18"
                                        fontWeight="bold"
                                        fill="#222B2E"
                                        fontFamily="Arial, sans-serif"
                                      >
                                        !
                                      </text>
                                    </svg>
                                    <Text
                                      size={mq.lt('md') ? 12 : 14}
                                      color="#222B2E"
                                      weight={500}
                                      style={{
                                        fontFamily:
                                          'Montserrat, Arial, sans-serif',
                                        letterSpacing: '0.5px',
                                      }}
                                    >
                                      <Markdown source={noBundleLabelHTML} />
                                    </Text>
                                  </NoBundleSelectedBox>
                                )
                              )}
                            </PriceBox>
                          </>
                        </SelectFlightBox>

                        {isNewFareSelectable && (
                          <FareOptionsColumn>
                            <FareOptionCard
                              type="button"
                              isUnavailable={!isNewFareSelectable}
                              newFareMode={newFareMode}
                              isActive={isNewFareActive}
                              isSelectedFare={isNewFareSelected}
                              highlightSelectedBundle={showAddedFareOptionBanner}
                              onClick={handleFareOptionClick}
                              data-hook={`flight-${flightOption.id}-new-fare-option`}
                              aria-label="New fare option"
                              disabled={!isNewFareSelectable || disabled}
                            >
                              {showAddedFareOptionBanner &&
                                isNewFareSelectable && (
                                  <FareOptionAddedBanner>
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <circle
                                        cx="12"
                                        cy="12"
                                        r="12"
                                        fill="#058027"
                                      />
                                      <path
                                        d="M7 13.5L10.5 17L17 10"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <FareOptionAddedText>
                                      <Markdown source={newFareAddedLabelHTML} />
                                    </FareOptionAddedText>
                                  </FareOptionAddedBanner>
                                )}
                              <FareOptionHeader>
                                <FareOptionLabel as="div" size={14}>
                                  <Markdown source={newFareLabelHTML} />
                                </FareOptionLabel>
                                {isNewFareSelectable && (
                                  <FareOptionChevron
                                    isOpen={isNewFarePanelOpen}
                                    aria-hidden="true"
                                  />
                                )}
                              </FareOptionHeader>
                              <FarePriceValueWrap center fullWidth>
                                <Price
                                  value={newFareCardPrice}
                                  currency={'$'}
                                  currencyFontSize={23}
                                  currencyFontWeight={700}
                                  decimalFontSize={20}
                                  decimalFontWeight={700}
                                  priceFontSize={44}
                                  priceFontWeight={700}
                                  color={'#01579B'}
                                />
                              </FarePriceValueWrap>
                            </FareOptionCard>
                          </FareOptionsColumn>
                        )}
                      </Box>
                      {mq.lte('md') && operatedBy && (
                        <FlightOperator
                          operatedBy={operatedBy}
                          operatedByLabel={operatedByLabel}
                          display="flex"
                          weight={500}
                        ></FlightOperator>
                      )}
                    </Box>
                  )}
                  {showBundlesInfo && (
                    <Box
                      mb={6}
                      automationHook={withValue(
                        FLIGHTS_PAGE_BUNDLE_OPTION_OPEN.hook,
                        `flight-${flightOption.id}`
                      )}
                    >
                      {!isNewFareView && (
                        <>
                          <Box backgroundColor="#F0F4FB" pb={4}>
                            <Media>
                              {(mq) => {
                                const isDesktop = mq.gte('md');

                                return (
                                  <>
                                    {!isDesktop ? (
                                      // Mobile/Responsive: show title with No Thanks button
                                      <BundleTitleRow>
                                        <BundleTitle
                                          data-hook={`flight-${flightOption.id}-BundleAndSaveTitle`}
                                          style={{
                                            marginBottom: 0,
                                            paddingTop: 0,
                                          }}
                                        >
                                          <Markdown
                                            source={
                                              leg === DEPARTING
                                                ? bundleAndSaveDepartingFlightLabelHTML
                                                : bundleAndSaveReturningFlightLabelHTML
                                            }
                                          />
                                        </BundleTitle>
                                        <NoThanksButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleNoBundle();
                                          }}
                                          data-hook={`flight-${flightOption.id}-noThanksButton`}
                                          style={{ alignSelf: 'flex-end' }}
                                        >
                                          <Markdown
                                            source={skipBundleButtonLabelHTML}
                                          />
                                        </NoThanksButton>
                                      </BundleTitleRow>
                                    ) : (
                                      // Desktop: show title with No Thanks button
                                      <Box
                                        d="flex"
                                        justifyContent="space-between"
                                      >
                                        <BundleTitle
                                          data-hook={`flight-${flightOption.id}-BundleAndSaveTitle`}
                                        >
                                          <Markdown
                                            source={
                                              leg === DEPARTING
                                                ? bundleAndSaveDepartingFlightLabelHTML
                                                : bundleAndSaveReturningFlightLabelHTML
                                            }
                                          />
                                        </BundleTitle>
                                        <NoThanksButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleNoBundle();
                                          }}
                                          data-hook={`flight-${flightOption.id}-noThanksButton`}
                                        >
                                          <Markdown
                                            source={skipBundleButtonLabelHTML}
                                          />
                                        </NoThanksButton>
                                      </Box>
                                    )}
                                  </>
                                );
                              }}
                            </Media>

                            <Media>
                              {(mq) =>
                                mq.lte('md') ? (
                                  <div>
                                    {bundleOptions.map((bundle) => {
                                      const isSelected =
                                        currentSelectedTier === bundle.tier &&
                                        validSelected &&
                                        !isCurrentLegNewFareSelected;
                                      const isBundleExpanded =
                                        expandedBundles.has(bundle.type);

                                      return (
                                        <MobileBundleCard
                                          key={bundle.type}
                                          isSelected={isSelected}
                                          data-hook={`flight-${flightOption.id}-bundleType-${bundle.type}-selected-${isSelected}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleBundleExpansion(bundle.type);
                                          }}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          <MobileBundleHeader>
                                            <div
                                              style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                width: '100%',
                                              }}
                                            >
                                              {/* First row: Bundle image, price, and ADD button */}
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent:
                                                    'space-between',
                                                  width: '100%',
                                                  marginBottom: '8px',
                                                }}
                                              >
                                                <TabBox
                                                  color={get(
                                                    theme.colors.bundle,
                                                    [
                                                      `tier_${bundle.tier}`,
                                                      'label',
                                                    ]
                                                  )}
                                                  style={{
                                                    marginRight: '12px',
                                                    width: '80px',
                                                    height: '35px',
                                                    minWidth: '70px',
                                                  }}
                                                >
                                                  <BundleImage
                                                    src={bundle.icon}
                                                    alt="Bundle icon"
                                                    style={{
                                                      maxWidth: '90%',
                                                    }}
                                                  />
                                                </TabBox>

                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginLeft: 'auto',
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      paddingRight: '12px',
                                                      fontSize: '14px',
                                                      fontWeight: '500',
                                                    }}
                                                  >
                                                    <p
                                                      style={{
                                                        margin: 0,
                                                        padding: 0,
                                                        fontSize: '24px',
                                                        fontWeight: '500',
                                                      }}
                                                    >
                                                      +${bundle.price}*
                                                    </p>
                                                    <div
                                                      style={{
                                                        fontSize: '12px',
                                                        fontWeight: '400',
                                                      }}
                                                    >
                                                      <Markdown
                                                        source={
                                                          bundlePageTextAssociatedWithCurrentPrice
                                                        }
                                                      />
                                                    </div>
                                                  </div>
                                                  {renderBundleAddButton(
                                                    bundle,
                                                    isSelected,
                                                    'addButton'
                                                  )}
                                                </div>
                                              </div>

                                              {/* Second row: Bundle subtitle (only when collapsed) */}
                                              {!isBundleExpanded && (
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    width: '100%',
                                                    textAlign: 'start',
                                                    flexDirection: 'column',
                                                    gap: '4px',
                                                  }}
                                                >
                                                  <MobileBundleSubTitle>
                                                    <Markdown
                                                      source={
                                                        bundle.mobileBundleAncillaries
                                                      }
                                                    />
                                                  </MobileBundleSubTitle>
                                                  {bundle.tier === 3 &&
                                                    bundle.features.some(
                                                      (feature) =>
                                                        feature.code ===
                                                        tripflexCode.ENHANCED_TRIP_FLEX
                                                    ) && (
                                                      <RefundPill>
                                                        <Markdown
                                                          source={
                                                            tripFlexOFOPLabelHTML
                                                          }
                                                        />
                                                      </RefundPill>
                                                    )}
                                                </div>
                                              )}
                                            </div>
                                          </MobileBundleHeader>
                                          {/* Show content based on individual bundle expansion OR global expansion */}
                                          {(isBundleExpanded ||
                                            isAllExpanded) && (
                                            <MobileBundleContent
                                              data-hook={`flight-${flightOption.id}-bundleType-${bundle.type}-content-expanded`}
                                            >
                                              {bundle.features.map(
                                                (feature, index) => (
                                                  <MobileBundleFeature
                                                    key={index}
                                                  >
                                                    <AncillaryImage
                                                      src={feature.icon}
                                                      style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        marginRight: '8px',
                                                        flexShrink: 0,
                                                      }}
                                                    />
                                                    <div
                                                      style={{
                                                        flex: 1,
                                                        fontSize: '12px',
                                                      }}
                                                    >
                                                      {feature.text &&
                                                        !feature.hasOnlySubtitle && (
                                                          <div
                                                            style={{
                                                              fontSize: '12px',
                                                              color: '#6B7280',
                                                              marginBottom:
                                                                '2px',
                                                              lineHeight: '1.2',
                                                            }}
                                                          >
                                                            <Markdown
                                                              source={
                                                                feature.text
                                                              }
                                                            />
                                                          </div>
                                                        )}
                                                      {feature.subtitle && (
                                                        <div
                                                          style={{
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            color: '#000',
                                                            lineHeight: '1.3',
                                                          }}
                                                        >
                                                          {typeof feature.subtitle ===
                                                          'string' ? (
                                                            <Markdown
                                                              source={
                                                                feature.subtitle
                                                              }
                                                            />
                                                          ) : (
                                                            feature.subtitle
                                                          )}
                                                        </div>
                                                      )}
                                                      {feature &&
                                                        feature.code ===
                                                          tripflexCode.ENHANCED_TRIP_FLEX &&
                                                        feature.ancillaryType ===
                                                          ancillaryTypes.TRIP_FLEX && (
                                                          <RefundPill
                                                            style={{
                                                              marginTop: '4px',
                                                            }}
                                                          >
                                                            <Markdown
                                                              source={
                                                                tripFlexOFOPLabelHTML
                                                              }
                                                            />
                                                          </RefundPill>
                                                        )}
                                                    </div>
                                                    {feature.info && (
                                                      <Popover
                                                        content={
                                                          <Box p={3}>
                                                            <Text
                                                              size={14}
                                                              weight={400}
                                                            >
                                                              <Markdown
                                                                source={
                                                                  feature.ancillaryType ===
                                                                  ancillaryTypes.TRIP_FLEX
                                                                    ? feature.code ===
                                                                      tripflexCode.ENHANCED_TRIP_FLEX
                                                                      ? tripFlexOFOPInfoDescriptionHTML
                                                                      : tripFlexInfoDescription
                                                                    :  feature.ancillaryType ===
                                                                        'PRIORITY_BOARDING'
                                                                      ? priorityBoardingInfoDescription
                                                                      : ''
                                                                }
                                                              />
                                                            </Text>
                                                          </Box>
                                                        }
                                                        label={'info-popover'}
                                                        arrowSize={
                                                          mq.lte('sm') ? 8 : 10
                                                        }
                                                        backgroundColor={
                                                          theme.colors.popover
                                                            .background
                                                        }
                                                        maxWidth="300px"
                                                        showClose={mq.lte('sm')}
                                                        placement="right-center"
                                                        trigger={
                                                          mq.lte('md')
                                                            ? 'click'
                                                            : 'hover'
                                                        }
                                                      >
                                                        <div
                                                          onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                          }}
                                                          style={{
                                                            display:
                                                              'inline-flex',
                                                            alignItems:
                                                              'center',
                                                            justifyContent:
                                                              'center',
                                                            padding: '4px',
                                                            cursor: 'pointer',
                                                          }}
                                                        >
                                                          <InfoIcon
                                                            src={infoIconImage}
                                                            alt="More info"
                                                            style={{
                                                              width: mq.lte(
                                                                'md'
                                                              )
                                                                ? '20px'
                                                                : '16px',
                                                              height: mq.lte(
                                                                'md'
                                                              )
                                                                ? '20px'
                                                                : '16px',
                                                              cursor: 'pointer',
                                                              flexShrink: 0,
                                                            }}
                                                          />
                                                        </div>
                                                      </Popover>
                                                    )}
                                                  </MobileBundleFeature>
                                                )
                                              )}
                                            </MobileBundleContent>
                                          )}
                                        </MobileBundleCard>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div>
                                    <BundleOptions>
                                      {bundleOptions.map((bundle) => {
                                        const isSelected =
                                          currentSelectedTier === bundle.tier &&
                                          validSelected &&
                                          !isCurrentLegNewFareSelected;
                                        return (
                                          <BundleOption
                                            key={bundle.type}
                                            isSelected={isSelected}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (isSelected) {
                                                handleNoBundle();
                                              } else {
                                                setFlightSelected();
                                                setFlightBundleInfoOpen(null);
                                                handleBundleSelection(
                                                  bundle.tier
                                                );
                                              }
                                            }}
                                            data-hook={`flight-${flightOption.id}-bundleType-${bundle.type}-selected-${isSelected}`}
                                          >
                                            <BundleHeader type={bundle.type}>
                                              <TabBox
                                                color={get(
                                                  theme.colors.bundle,
                                                  [
                                                    `tier_${bundle.tier}`,
                                                    'label',
                                                  ]
                                                )}
                                              >
                                                <Box
                                                  d="flex"
                                                  alignItems="center"
                                                  justifyContent="center"
                                                >
                                                  <BundleImage
                                                    src={bundle.icon}
                                                    alt="Bundle icon"
                                                  />
                                                </Box>
                                              </TabBox>
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  alignItems: 'center',
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    fontSize: '24px',
                                                    fontWeight: '500',
                                                  }}
                                                >
                                                  +${bundle.price}*
                                                </span>
                                                <div
                                                  style={{
                                                    fontSize: '12px',
                                                    fontWeight: '400',
                                                  }}
                                                >
                                                  <Markdown
                                                    source={
                                                      bundlePageTextAssociatedWithCurrentPrice
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            </BundleHeader>
                                            <BundleContent>
                                              {bundle.features.map(
                                                (feature, index) => (
                                                  <BundleFeature
                                                    key={index}
                                                    style={{
                                                      borderBottom:
                                                        index !==
                                                        bundle.features.length -
                                                          1
                                                          ? '1px solid #E5E7EB'
                                                          : 'none',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent:
                                                        'space-between',
                                                      minHeight: '50px',
                                                      padding: '0 0',
                                                    }}
                                                  >
                                                    <AncillaryImage
                                                      src={feature.icon}
                                                      isBagIcon={
                                                        feature.text ===
                                                        bagsIncludeLabelHTML
                                                      }
                                                    />
                                                    <div
                                                      style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        flex: 1,
                                                      }}
                                                    >
                                                      {feature.text &&
                                                        !feature.hasOnlySubtitle && (
                                                          <FeatureText
                                                            style={{
                                                              fontSize: '12px',
                                                              fontWeight: '400',
                                                              marginBottom:
                                                                feature.subtitle
                                                                  ? '4px'
                                                                  : '0',
                                                            }}
                                                          >
                                                            <Markdown
                                                              source={
                                                                feature.text
                                                              }
                                                            />
                                                          </FeatureText>
                                                        )}
                                                      {feature.subtitle && (
                                                        <FeatureText
                                                          style={{
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                          }}
                                                        >
                                                          {typeof feature.subtitle ===
                                                          'string' ? (
                                                            <Markdown
                                                              source={
                                                                feature.subtitle
                                                              }
                                                            />
                                                          ) : (
                                                            feature.subtitle
                                                          )}
                                                        </FeatureText>
                                                      )}
                                                      {feature &&
                                                        feature.code ===
                                                          tripflexCode.ENHANCED_TRIP_FLEX &&
                                                        feature.ancillaryType ===
                                                          ancillaryTypes.TRIP_FLEX && (
                                                          <RefundPill>
                                                            <Markdown
                                                              source={
                                                                tripFlexOFOPLabelHTML
                                                              }
                                                            />
                                                          </RefundPill>
                                                        )}
                                                    </div>
                                                    {feature.info && (
                                                      <Popover
                                                        content={
                                                          <Box p={3}>
                                                            <Text
                                                              size={14}
                                                              weight={400}
                                                            >
                                                              <Markdown
                                                                source={
                                                                  feature.ancillaryType ===
                                                                  ancillaryTypes.TRIP_FLEX
                                                                    ? feature.code ===
                                                                        tripflexCode.ENHANCED_TRIP_FLEX
                                                                      ? tripFlexOFOPInfoDescriptionHTML
                                                                      : tripFlexInfoDescription
                                                                    : feature.ancillaryType ===
                                                                        'PRIORITY_BOARDING'
                                                                      ? priorityBoardingInfoDescription
                                                                      : ''
                                                                }
                                                              />
                                                            </Text>
                                                          </Box>
                                                        }
                                                        label={'info-popover'}
                                                        arrowSize={10}
                                                        backgroundColor={
                                                          theme.colors.popover
                                                            .background
                                                        }
                                                        maxWidth="300px"
                                                        showClose={mq.lte('sm')}
                                                        placement="right-center"
                                                      >
                                                        <InfoIcon
                                                          src={infoIconImage}
                                                          alt="More info"
                                                        />
                                                      </Popover>
                                                    )}
                                                  </BundleFeature>
                                                )
                                              )}
                                            </BundleContent>
                                            <div
                                              style={{
                                                padding: '12px 16px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                              }}
                                            >
                                              {renderBundleAddButton(
                                                bundle,
                                                isSelected,
                                                'addButton-desktop'
                                              )}
                                            </div>
                                          </BundleOption>
                                        );
                                      })}
                                    </BundleOptions>
                                  </div>
                                )
                              }
                            </Media>
                          </Box>
                        </>
                      )}
                      <Box backgroundColor="#F0F4FB" margintop={-2}>
                        {isNewFareView &&
                          isNewFareSelectable &&
                          newFareBundle && (
                            <NewFareModeWrapper>
                              <NewFareMobileLabel>
                                {leg === DEPARTING
                                  ? newFareDepartureResponsiveLabelHTML
                                  : newFareReturnResponsiveLabelHTML}
                              </NewFareMobileLabel>
                              <NewFareCard
                                onClick={handleNewFareCardClick}
                                onKeyDown={handleNewFareCardKeyDown}
                                role="button"
                                tabIndex={
                                  isNewFareSelectable && !disabled ? 0 : -1
                                }
                                aria-pressed={isCurrentLegNewFareSelected}
                                aria-disabled={!isNewFareSelectable || disabled}
                              >
                                <NewFareImageWrapper>
                                  <NewFareCardImage
                                    src={
                                      useNewFareImages(data).newFareBundleBanner
                                    }
                                    alt="New Fare image"
                                  />
                                  <NewFareImageTint />
                                  <NewFareImageTextList>
                                    {newFareBenefitsFirstPointLabelHTML && (
                                      <NewFareImageTextItem>
                                        <img
                                          src={newFareFeatureIconCheckmark}
                                          alt=""
                                        />
                                        <Markdown
                                          source={
                                            newFareBenefitsFirstPointLabelHTML
                                          }
                                        />
                                      </NewFareImageTextItem>
                                    )}
                                    {newFareBenefitsSecondPointLabelHTML && (
                                      <NewFareImageTextItem>
                                        <img
                                          src={newFareFeatureIconCheckmark}
                                          alt=""
                                        />
                                        <Markdown
                                          source={
                                            newFareBenefitsSecondPointLabelHTML
                                          }
                                        />
                                      </NewFareImageTextItem>
                                    )}
                                    {newFareBenefitsThirdPointLabelHTML && (
                                      <NewFareImageTextItem>
                                        <img
                                          src={newFareFeatureIconCheckmark}
                                          alt=""
                                        />
                                        <Markdown
                                          source={
                                            newFareBenefitsThirdPointLabelHTML
                                          }
                                        />
                                      </NewFareImageTextItem>
                                    )}
                                  </NewFareImageTextList>
                                  <NewFareImageBottomTint />
                                  <NewFareImageBottomTextList>
                                    {newFareBannerTitleHTML && (
                                      <NewFareImageBottomTitle>
                                        <Markdown
                                          source={newFareBannerTitleHTML}
                                        />
                                      </NewFareImageBottomTitle>
                                    )}
                                    {newFareBannerDescriptionHTML && (
                                      <NewFareImageBottomSubtitle>
                                        <Markdown
                                          source={newFareBannerDescriptionHTML}
                                        />
                                      </NewFareImageBottomSubtitle>
                                    )}
                                  </NewFareImageBottomTextList>
                                </NewFareImageWrapper>
                                <NewFareCardContent
                                  isSelected={isCurrentLegNewFareSelected}
                                >
                                  <NewFareCardHeader>
                                    <div>
                                      <NewFareCardTitle>
                                        {leg === DEPARTING ? (
                                          <Markdown
                                            source={newFareDepartureLabelHTML}
                                          />
                                        ) : (
                                          <Markdown
                                            source={newFareReturnLabelHTML}
                                          />
                                        )}
                                      </NewFareCardTitle>
                                    </div>
                                  </NewFareCardHeader>
                                  <NewFareFeatureGrid>
                                    {newFareFeatures.map((feature, idx) => (
                                      <React.Fragment key={idx}>
                                        <NewFareFeatureCell
                                          ancillaryType={feature.ancillaryType}
                                        >
                                          <NewFareCellIcon
                                            src={
                                              feature.ancillaryType ===
                                              'TRIP_FLEX'
                                                ? useNewFareImages(data)
                                                    .enhancedTripFlexIcon
                                                : feature.icon
                                            }
                                            ancillaryType={
                                              feature.ancillaryType
                                            }
                                            alt=""
                                          />
                                          <div
                                            style={{
                                              flex: '1 1 auto',
                                              minWidth: 0,
                                              display: 'flex',
                                              flexDirection: 'column',
                                            }}
                                          >
                                            <NewFareCellLabel>
                                              {feature.text ? (
                                                <Markdown
                                                  source={feature.text}
                                                />
                                              ) : null}
                                            </NewFareCellLabel>
                                            <NewFareCellValue>
                                              {feature.ancillaryType ===
                                              'TRIP_FLEX' ? (
                                                newFareTripflexLabelHTML ? (
                                                  <Markdown
                                                    source={
                                                      newFareTripflexLabelHTML
                                                    }
                                                  />
                                                ) : null
                                              ) : typeof feature.subtitle ===
                                                'string' ? (
                                                <Markdown
                                                  source={feature.subtitle}
                                                />
                                              ) : (
                                                feature.subtitle
                                              )}
                                            </NewFareCellValue>
                                            {feature.ancillaryType ===
                                              'TRIP_FLEX' && (
                                              <TripFlexNewFareInfoBox>
                                                {newFareRefundBenefitLabelHTML}
                                              </TripFlexNewFareInfoBox>
                                            )}
                                          </div>
                                          {feature.info && (
                                            <Popover
                                              content={
                                                <Box p={2}>
                                                  <Text size={14} weight={400}>
                                                    {feature.ancillaryType ===
                                                    'TRIP_FLEX' ? (
                                                      tripFlexOFOPInfoDescriptionHTML
                                                    ) : feature.ancillaryType ===
                                                      'PRIORITY_BOARDING' ? (
                                                      priorityBoardingInfoDescription
                                                    ) : (
                                                      <Markdown
                                                        source={
                                                          newFareBagsTooltipTextHTML
                                                        }
                                                      />
                                                    )}
                                                  </Text>
                                                </Box>
                                              }
                                              label="popover"
                                              arrowSize={10}
                                              backgroundColor={
                                                theme.colors.popover.background
                                              }
                                              placement={'bottom-end'}
                                              maxWidth="300px"
                                              showClose={false}
                                            >
                                              <InfoIcon
                                                src={infoIconImage}
                                                alt="More info"
                                              />
                                            </Popover>
                                          )}
                                        </NewFareFeatureCell>
                                      </React.Fragment>
                                    ))}
                                  </NewFareFeatureGrid>
                                  <NewFareSelectButtonWrapper>
                                    <NewFareSelectButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleNewFareCardClick(e);
                                      }}
                                      isSelected={isCurrentLegNewFareSelected}
                                    >
                                      {isCurrentLegNewFareSelected ? (
                                        <svg
                                          width="22"
                                          height="22"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <circle
                                            cx="12"
                                            cy="12"
                                            r="12"
                                            fill="none"
                                          />
                                          <path
                                            d="M4 13L9 18L20 7"
                                            stroke="#ffffff"
                                            strokeWidth="3"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      ) : (
                                        <>
                                          <span className="btn-text">
                                            {selectButtonLabelHTML}
                                          </span>
                                        </>
                                      )}
                                    </NewFareSelectButton>
                                  </NewFareSelectButtonWrapper>
                                </NewFareCardContent>
                              </NewFareCard>
                            </NewFareModeWrapper>
                          )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </FlightDetailsBox>
            </FlightBox>
          </FlightWrapper>
        )}
      </Media>
    );
  }
);
FlightItemWithAllegiantFirst.propTypes = propTypes;

export default FlightItemWithAllegiantFirst;